// frontend/src/context/LanguageContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';

// Define available languages
export const LANGUAGES = {
  ES: 'es', // Spanish (default)
  EN: 'en', // English
  PT: 'pt'  // Portuguese (future support)
};

// Translation data
const translations = {
  [LANGUAGES.EN]: {
    // Navigation
    nav_home: 'Home',
    nav_dashboard: 'Dashboard',
    nav_login: 'Login',
    nav_register: 'Register',
    nav_logout: 'Logout',
    nav_signup: 'Sign Up',
    nav_account: 'Account',
    nav_levels: 'Levels',
    nav_access_requests: 'Access Requests',
    courses: 'Courses',
    site_title: 'English with Profezac',
    
    // Levels
    level_basic: 'Basic',
    level_advanced: 'Advanced',
    level_native: 'Native',
    level_all: 'All Levels',
    
    // Home page
    home_title: 'Master English at Your Own Pace',
    home_subtitle: 'Join our comprehensive English course with daily video lessons at Basic, Advanced, and Native levels. Improve your skills with interactive quizzes and track your progress.',
    home_get_started_free: 'Get Started Free',
    home_login: 'Login',
    home_try_demos: 'Or try our free video demonstrations:',
    home_demo_title: 'Try Our Free Video and Quiz Demonstrations',
    home_demo_subtitle: 'Experience each level with our basketball lesson samples',
    not_ready_sign_up: '(not ready to sign up yet?)',
    
    // Level cards
    basic_level: 'Basic Level',
    advanced_level: 'Advanced Level',
    native_level: 'Native Level',
    basketball_basics: 'Basketball Basics',
    basketball_techniques: 'Basketball Techniques', 
    basketball_stories: 'Basketball Stories',
    basketball_basics_desc: 'Learn simple vocabulary: "This is a basketball. I dribble the basketball."',
    basketball_techniques_desc: '"This is a layup. When you miss and catch the ball, that\'s a rebound."',
    basketball_stories_desc: '"I got cut from the high school team. Basketball favors height..."',
    
    // Feature lists
    simple_vocabulary: 'Simple vocabulary',
    clear_pronunciation: 'Clear pronunciation',
    basic_sentence_patterns: 'Basic sentence patterns',
    technical_vocabulary: 'Technical vocabulary',
    detailed_explanations: 'Detailed explanations',
    complex_sentences: 'Complex sentences',
    natural_conversation: 'Natural conversation',
    personal_stories: 'Personal stories',
    cultural_context: 'Cultural context',
    
    // Demo buttons
    watch_basic_demo: 'Watch Basic Demo',
    watch_advanced_demo: 'Watch Advanced Demo', 
    watch_native_demo: 'Watch Native Demo',
    
    // Level descriptions
    basic_description: 'Perfect for beginners. Learn essential vocabulary and grammar to start your English journey.',
    advanced_description: 'For intermediate learners. Improve your fluency with more complex structures and vocabulary.',
    native_description: 'Master the nuances of English with idioms, cultural references, and advanced communication skills.',
    learn_more: 'Learn More',

    // Dashboard
    dashboard_title: 'Dashboard',
    welcome: 'Welcome',
    dashboard_intro: 'Access your English learning content here.',
    subscription_status: 'Subscription Status',
    subscription_active_message: 'Your subscription is active!',
    access_status_message: 'You have access!',
    subscription_inactive_message: 'You don\'t have an active subscription.',
    subscription_cancelled_notice: 'Your subscription has been cancelled. You maintain access until the expiration date.',
    subscription_level: 'Level',
    subscription_level_basic: 'Basic',
    subscription_level_advanced: 'Advanced', 
    subscription_level_native: 'Native',
    subscription_level_all: 'All Levels',
    subscription_expires: 'Expires',
    payment_method: 'Payment Method',
    manage_subscription: 'Manage Subscription',
    get_subscription: 'Get Subscription',
    start_secure_checkout: 'Start Secure Checkout',
    renew_subscription: 'Renew Subscription',
    reactivate_subscription: 'Reactivate Subscription',
    subscription_cancelled: 'Subscription Cancelled',
    subscription_cancelled_message: 'You still have access until',
    cancelled_on: 'Cancelled on',
    access_until: 'Access until',
    subscription_reactivate: 'Reactivate',
    subscription_reactivating: 'Reactivating...',
    subscription_error_reactivate: 'Error reactivating subscription. Please try again.',
    reactivate_current_subscription: 'Reactivate Current Subscription',
    get_new_subscription: 'Get New Subscription',
    change_plan: 'Change Plan',
    subscription_status_active: 'Active',
    subscription_status_cancelled: 'Cancelled',
    subscription_expired_message: 'Your subscription expired on',
    previous_payment_method: 'Previous Payment Method',
    learning_levels: 'Learning Levels',
    course_access: 'Course Access',
    complete_english_course: 'Complete English Course',
    course_access_description: 'Access all 180 essential English words organized by level.\nMaster vocabulary with Professor Zac\'s comprehensive video lessons.',
    total_words: 'Total Words',
    skill_levels: 'Skill Levels',
    practice_quizzes: 'Practice Quizzes',
    start_learning_now: 'Start Learning Now',
    full_access_all_levels: 'You have full access to all levels!',
    access_basic_advanced_native: 'Access to Basic, Advanced & Native',
    access_basic_advanced: 'Access to Basic & Advanced',
    access_basic_level: 'Access to Basic level',
    signup_start_journey: 'Sign up to start your English journey',
    
    // Video card descriptions - fallback text
    learn_word_with_professor: 'Learn this word with Professor Zac',
    
    // Dynamic description translations
    learn_the_activity: 'Learn the activity',
    learn_conversation_about: 'Learn conversation about',
    learn_the_verb: 'Learn the verb',
    learn_the_noun: 'Learn the noun',
    learn_the_adjective: 'Learn the adjective',
    learn_the_adverb: 'Learn the adverb',
    learn_the_preposition: 'Learn the preposition',
    learn_the_word: 'Learn the',
    in_level_english: 'in %s level English',
    
    // Archive page text
    all_words_complete_archive: 'All 180 Words - Complete Archive',
    browse_all_english_words: 'Browse all English words across all levels',
    back_to_dashboard: 'Back to Dashboard',
    search_words_placeholder: 'Search words...',
    your_access_level: 'Your Access Level',
    all_progress: 'All Progress',
    not_started: 'Not Started',
    in_progress: 'In Progress',
    completed: 'Completed',
    words_found: 'Words Found',
    no_words_found: 'No words found',
    try_adjusting_filters: 'Try adjusting your search or filter criteria',
    watch: 'Watch',
    upgrade: 'Upgrade',
    upgrade_for_full_access: 'Upgrade for Full Access',
    filter_by_progress: 'Filter by Progress:',
    learn_about_levels: 'Learn About Levels',
    level_basic_desc: 'Foundational English for beginners.',
    level_advanced_desc: 'Intermediate to advanced English practice.',
    level_native_desc: 'Authentic native speaker content.',
    explore_level: 'Explore Level',
    free_preview: 'Free Preview',
    unlock_all: 'Unlock All',
    
    // Free value proposition
    profe_confidence_message: 'Profe Zac is so confident that you\'ll love learning English with his videos that he\'s giving away free access (an $8 value)!',
    free_access_includes: 'Your FREE access includes:',
    free_video_lessons: '3 Video Lessons + Interactive Quizzes',
    free_all_levels: '(Basic, Advanced & Native levels)',
    free_daily_inspiration: 'Daily Inspiration Videos',
    free_progress_tracking: 'Progress Tracking Dashboard',
    free_no_payment: 'No payment required - Start immediately!',
    free_no_commitment: 'No commitment whatsoever',
    free_value_amount: '$8 Value - Yours FREE',
    
    // Freemium content
    freemium_header: 'Free Preview',
    freemium_subtitle: 'sample videos',
    freemium_viewing_message: 'You\'re viewing',
    freemium_from_collection: 'from our',
    freemium_level_collection: 'level collection',
    freemium_upgrade_message: 'Upgrade to access all',
    freemium_videos_and_quizzes: 'videos and unlimited quizzes!',
    freemium_upgrade_now: 'Upgrade Now',
    freemium_quiz_attempts: 'Quiz Attempts',
    freemium_quiz_limit_reached: 'Quiz Limit Reached',
    freemium_available_videos: 'Available Videos',
    freemium_want_access: 'Want access to all',
    freemium_unlock_videos: 'Unlock All Videos',
    freemium_select_video: 'Select a video to start learning',
    freemium_choose_from: 'Choose from the',
    freemium_available_videos_text: 'available freemium videos',
    freemium_interactive_quiz: 'Interactive Quiz',
    freemium_attempts_used: 'attempts used',
    freemium_quiz_limit_message: 'You\'ve used all your free quiz attempts.',
    freemium_upgrade_unlimited: 'Upgrade for Unlimited Quizzes',
    loading_freemium_content: 'Loading freemium content...',
    loading_quiz: 'Loading quiz...',
    quiz_error: 'Quiz Error',
    
    // Access Level Management
    access_level_management: 'Access Level Management',
    request_access_change: 'Request Access to Different Level',
    cancel_request: 'Cancel Request',
    view_my_requests: 'View My Requests',
    hide_my_requests: 'Hide',
    your_access_change_requests: 'Your Access Change Requests',
    no_access_requests: 'No access change requests found.',
    request_submitted_success: 'Your access change request has been submitted successfully! An admin will review it shortly.',
    request_submit_failed: 'Failed to submit request',
    network_error: 'Network error. Please try again.',
    request_access_change_form: 'Request Access Change',
    requested_access_level: 'Requested Access Level',
    select_level: 'Select a level...',
    reason_for_request: 'Reason for Request',
    reason_placeholder: 'Please explain why you need this access level change. Include details about your subscription or any issues you\'re experiencing...',
    characters: 'characters',
    
    // New adaptive translations
    request_access: 'Request Access',
    change_access_level: 'Change Access Level',
    no_active_access_message: 'You don\'t currently have active access. Request access to start learning!',
    request_access_form: 'Request Access',
    request_access_help_text: 'If you believe you should have access or are experiencing issues with your subscription, please submit a request below.',
    request_access_reason_placeholder: 'Please explain why you need access. For example: "I paid but don\'t see my subscription" or "My subscription expired but I want to continue learning"...',
    your_subscription_info: 'Your Subscription Info',
    current_level: 'Current Level',
    other_levels: 'Other Levels',
    view_level: 'View %s Level',
    upgrade_required_level: 'Upgrade Required for %s',
    access_denied_level: 'Access denied for %s level. Please upgrade your subscription.',
    access_denied_title: 'Access Denied',
    access_denied_message: 'You don\'t have access to the %s level.',
    upgrade_to_access_message: 'Upgrade your membership to unlock all levels and continue learning.',
    upgrade_membership: 'Upgrade Membership',
    back: 'Back',
    cancel: 'Cancel',
    submit_request: 'Submit Request',
    submitting: 'Submitting...',
    requested: 'Requested',
    reason: 'Reason',
    admin_notes: 'Admin Notes',
    basic_level_desc_short: 'Fundamental English learning content',
    advanced_level_desc_short: 'More challenging English content',
    native_level_desc_short: 'Native-speaker level content',
    all_levels_desc: 'Access to all content levels',
    your_subscription: 'Your subscription',
    today_lessons: "Today's Lessons",
    today_video: "Today's Video Lesson",
    watch_video: 'Watch Video',
    video_archives: 'Video Archives',
    access_previous: 'Access Previous Lessons',
    browse_archive: 'Browse through our archive of video lessons by level.',
    
    // Auth
    email_address: 'Email Address',
    password: 'Password',
    confirm_password: 'Confirm Password',
    first_name: 'First Name', 
    last_name: 'Last Name',
    already_account: 'Already have an account?',
    no_account: "Don't have an account?",
    register_here: 'Register here',
    login_here: 'Login here',
    forgot_password: 'Forgot password?',
    logging_in: 'Logging in...',
    registering: 'Registering...',
    
    // Videos
    back_to_dashboard: 'Back to Dashboard',
    video_of_day: "Today's %s English Lesson",
    description: 'Description',
    published_on: 'Published on',
    view_archive: 'View Archive',
    browse_archive_level: 'Browse %s Archive',
    no_video: 'No video available',
    error_loading: 'Error loading video',
    try_again: 'Try Again',
    about_lesson: 'About this lesson',
    learn_word: 'Learn "%s" in %s level English',
    word_type_noun: 'the noun',
    word_type_verb: 'the verb',
    word_type_adjective: 'the adjective', 
    word_type_preposition: 'the preposition',
    word_type_adverb: 'the adverb',
    
    // Transcription
    transcription_locked: 'Transcription Locked',
    complete_quiz_unlock: 'Complete the quiz for this video to unlock the transcription',
    
    // Quiz
    take_quiz: 'Take Quiz',
    quiz_results: 'Quiz Results',
    test_understanding: 'Test Your Understanding',
    submit_quiz: 'Submit Quiz',
    next_question: 'Next Question',
    previous_question: 'Previous Question',
    hide_quiz: 'Hide Quiz',
    generate_quiz: 'Generate Quiz',
    loading_quiz: 'Loading Quiz...',
    generating_quiz: 'Generating Quiz...',
    watch_segment: 'Watch Segment',
    pause_segment: 'Pause Segment',
    press_again_if_cut_short: '💡 Press again if cut short',
    
    // Quiz instructions
    arrange_words_instruction: 'Arrange these words in the correct order to match what you heard in the video:',
    select_correct_answers_instruction: 'Based on what you saw in the video, select all correct answers:',
    
    your_score: 'Your Score',
    answered_questions: 'You answered %s out of %s questions correctly.',
    question_review: 'Question Review',
    question: 'Question',
    of: 'of',
    correct: 'Correct',
    incorrect: 'Incorrect',
    your_answer: 'Your answer',
    correct_answer: 'Correct answer',
    explanation: 'Explanation',
    no_answer: '[No answer]',
    error_during_submission: 'Error during submission',
    back_to_video: 'Back to Video',
    quiz_not_found: 'Quiz Not Found',
    quiz_not_found_message: 'The requested quiz could not be found or loaded.',
    loading_question: 'Loading question...',
    go_to_question: 'Go to question',
    error: 'Error',
    submitting: 'Submitting...',
    
    // Subscription status
    active: 'active',
    expired: 'expired',
    cancelled: 'cancelled',
    pending: 'pending',
    
    // Footer
    footer_rights: 'All Rights Reserved',
    footer_privacy: 'Privacy Policy',
    footer_terms: 'Terms of Use',
    footer_contact: 'Contact Us',
    
    // VideoArchive
    filter_by_type: 'Filter by type',
    all_types: 'All Types',
    showing_videos: 'Showing %s of %s videos',
    no_videos_found: 'No videos found for this level and type.',
    general: 'General',
    previous: 'Previous',
    next: 'Next',
    
    // Levels Explained Page
    levels_explained_title: 'Master English at Your Own Pace',
    levels_explained_subtitle: 'Join our comprehensive English course with daily video lessons at Basic, Advanced, and Native levels. Improve your skills with interactive quizzes and track your progress.',
    basketball_example_intro: 'Example: Learning "Basketball"',
    basketball_example_desc: 'See how the same topic is taught across all three levels. Each level builds upon the previous one, providing the perfect progression for your English learning journey.',
    what_youll_learn: 'What You\'ll Learn:',
    basketball_example: 'Basketball Example:',
    perfect_for: 'Perfect for:',
    watch_sample_video: 'Watch Sample Video',
    free_demo_signup: 'Free demo - Sign up to access full content',
    how_learning_works: 'How Our Learning System Works',
    watch_videos: 'Watch Videos',
    watch_videos_desc: 'Watch daily video lessons tailored to your level with clear explanations and real-world examples.',
    take_quizzes: 'Take Quizzes',
    take_quizzes_desc: 'Complete interactive quizzes that adapt to your level and help reinforce what you\'ve learned.',
    track_progress: 'Track Progress',
    track_progress_desc: 'Monitor your improvement and unlock transcriptions as you master each lesson.',
    ready_to_start: 'Ready to Start Learning?',
    ready_to_start_desc: 'Join thousands of students improving their English with our proven method. Choose your level and start your journey today!',
    sign_up_free: 'Sign Up Free',
    login: 'Login',
    faq_title: 'Frequently Asked Questions',
    faq_which_level: 'Which level should I start with?',
    faq_which_level_answer: 'Start with Basic if you\'re new to English, Advanced if you can have simple conversations, or Native if you want to perfect your fluency with authentic content.',
    faq_multiple_levels: 'Can I access multiple levels?',
    faq_multiple_levels_answer: 'Yes! Our subscription plans allow access to your chosen level and all lower levels. The "All Levels" plan gives you complete access to all content.',
    faq_how_quizzes: 'How do the quizzes work?',
    faq_how_quizzes_answer: 'After watching a video, you\'ll take an interactive quiz based on the content. Pass the quiz to unlock the video transcription and track your progress.',
    
    // Level specific content
    basic_level_title: 'Basic Level',
    basic_level_description: 'Perfect for beginners learning foundational English vocabulary and simple sentence structures.',
    basic_features_1: 'Simple vocabulary introduction',
    basic_features_2: 'Clear pronunciation',
    basic_features_3: 'Basic sentence patterns',
    basic_features_4: 'Repetition for learning',
    basic_example_text: 'This is a BASKETBALL. A BASKETBALL. This is a BASKETBALL COURT. I DRIBBLE the BASKETBALL. I SHOOT the BASKETBALL.',
    basic_audience: 'Beginners with little to no English experience',
    
    advanced_level_title: 'Advanced Level',
    advanced_level_description: 'For intermediate learners ready to expand vocabulary and understand more complex concepts.',
    advanced_features_1: 'Advanced vocabulary',
    advanced_features_2: 'Detailed explanations',
    advanced_features_3: 'Complex sentence structures',
    advanced_features_4: 'Conditional and contextual learning',
    advanced_example_text: 'This is a layup. This is a rebound. When you shoot and you miss and you catch the ball, that\'s a rebound. A jump shot is when you jump and you shoot.',
    advanced_audience: 'Intermediate learners comfortable with basic English',
    
    native_level_title: 'Native Level',
    native_level_description: 'Authentic native speaker content with natural conversation, idioms, and cultural context.',
    native_features_1: 'Natural conversation flow',
    native_features_2: 'Personal experiences and stories',
    native_features_3: 'Cultural context and nuances',
    native_features_4: 'Complex grammar and expressions',
    native_example_text: 'I really enjoy basketball because when you play basketball, you don\'t have to think about anything else. Basketball is a sport that favors height. I got cut from the high school team.',
    native_audience: 'Advanced learners seeking native-like fluency',
    
    // Course Demo Page
    course_demo_title: '%s Demo',
    course_demo_subtitle: 'Experience our %s level content with this real basketball lesson',
    basketball_level_title: 'Basketball - %s',
    sample_content: 'Sample Content:',
    try_interactive_quiz: 'Try the Interactive Quiz',
    interactive_quiz_title: 'Interactive Quiz: %s',
    interactive_quiz_preview: 'Interactive Quiz Preview',
    quiz_preview_intro: 'After watching the video, you\'ll take a quiz with questions like:',
    sample_question: 'Sample Question:',
    complete_quiz_unlock_transcript: 'Complete the quiz to unlock the full transcript with word-by-word timestamps!',
    what_youll_learn_level: 'What You\'ll Learn at %s',
    ready_to_start_learning: 'Ready to Start Learning?',
    course_demo_cta_text: 'This is just a preview! Sign up to access hundreds of videos across all levels.',
    compare_all_levels: 'Compare all levels',
    loading_demo_content: 'Loading demo content...',
    error_loading_demo: 'Error Loading Demo',
    try_again: 'Try Again',
    level_not_found: 'Level not found',
    real_video_streaming: 'Real video streaming available in full version',
    demo_mode_notice: 'Demo mode: Database connection required for actual video',
    fully_functional_demo: 'This is a fully functional demo! In the full version, completing quizzes unlocks transcripts with word-by-word timestamps.',
    
    // Course Demo Level Descriptions  
    basic_video_description: 'In this basketball lesson, you\'ll learn basic vocabulary like \'basketball\', \'court\', and \'goal\'. The teacher speaks slowly and clearly, repeating each word multiple times to help you memorize and pronounce correctly.',
    advanced_video_description: 'This advanced basketball lesson introduces technical terms like \'layup\', \'rebound\', and \'jump shot\'. You\'ll learn explanations and conditions (when X happens, then Y) to understand basketball mechanics.',
    native_video_description: 'Experience authentic native speech about basketball. The teacher shares personal stories about being cut from the high school team and discusses how height affects basketball performance - real cultural insights you won\'t find in textbooks.',
    
    // Quiz Questions - Basic Level
    basic_quiz_question_1: 'Arrange these words in the correct order:',
    basic_quiz_question_2: 'What do you do with a basketball?',
    basic_quiz_answer_dribble: 'Dribble',
    basic_quiz_answer_eat: 'Eat',
    basic_quiz_answer_read: 'Read',
    basic_quiz_answer_sleep: 'Sleep',
    
    // Quiz Questions - Advanced Level  
    advanced_quiz_question_1: 'What is a rebound in basketball?',
    advanced_quiz_answer_1_correct: 'When you catch the ball after a missed shot',
    advanced_quiz_answer_1_wrong_1: 'When you score points',
    advanced_quiz_answer_1_wrong_2: 'When you pass the ball',
    advanced_quiz_answer_1_wrong_3: 'When you start the game',
    advanced_quiz_question_2: 'A jump shot is when you [BLANK 1] and you [BLANK 2].',
    
    // Quiz Questions - Native Level
    native_quiz_question_1: 'What does "got cut from the team" mean?',
    native_quiz_answer_1_correct: 'Was removed from the team',
    native_quiz_answer_1_wrong_1: 'Was injured playing',
    native_quiz_answer_1_wrong_2: 'Won the championship',
    native_quiz_answer_1_wrong_3: 'Became the captain',
    native_quiz_question_2: 'Why does the speaker say basketball "favors height"?',
    native_quiz_answer_2_correct: 'Taller players have advantages',
    native_quiz_answer_2_wrong_1: 'Basketball courts are high',
    native_quiz_answer_2_wrong_2: 'The ball is heavy',
    native_quiz_answer_2_wrong_3: 'The game is difficult',
    
    // Subscription Management Page
    subscription_title: 'Subscription Management',
    subscription_login_required: 'Login Required',
    subscription_login_message: 'You need to log in to manage your subscription',
    subscription_error_redirect: 'Error redirecting to payment portal',
    subscription_confirm_cancel: 'Are you sure you want to cancel your subscription?',
    subscription_motivational_title: 'Before You Cancel - Remember This',
    subscription_motivational_message: 'Remember, learning English is a process. It doesn\'t happen overnight. Don\'t look for immediate fluency, but instead look for improving every single day, even if that could mean just learning a new word. One brick is not much, but if you add a new brick every day, that is how you build a house.',
    subscription_motivational_continue: 'Keep Learning',
    subscription_motivational_cancel: 'Cancel Anyway',
    subscription_error_cancel: 'Error cancelling subscription',
    subscription_cancelled_pending: 'Subscription Cancelled',
    subscription_cancelled_message: 'Your subscription has been cancelled but you still have access until:',
    subscription_cancelled_on: 'Cancelled on',
    subscription_active: 'Active Subscription',
    subscription_level: 'Level',
    subscription_access_until: 'Access Until',
    subscription_expires: 'Expires',
    subscription_processor: 'Payment Method',
    subscription_resubscribe: 'Subscribe Again',
    subscription_extend: 'Extend / Upgrade Access',
    subscription_upgrade: 'Upgrade / Change Plan',
    subscription_cancelling: 'Cancelling...',
    subscription_cancel: 'Cancel Subscription',
    subscription_manual_payment_info: 'Your access will automatically expire on %s. To extend your access, simply make another payment using the button above.',
    subscription_manual_payment_label: 'Manual Payment:',
    subscription_inactive: 'No Active Subscription',
    subscription_inactive_message: 'You don\'t have an active subscription. Subscribe to get access to all learning content.',
    subscription_loading: 'Loading...',
    subscription_subscribe: 'Get Subscription (Stripe/PayPal)',
    subscription_mercadopago_manual: 'Manual MercadoPago Payment',
    subscription_help_text: 'Need help with your subscription? Contact us at ',
    payment_divider_or: 'or',
    
    // Manual payment status
    pending_payment_review: 'Payment Under Review',
    payment_under_review: 'Payment Under Review',
    manual_payment_review_message: 'Your manual payment proof has been received and is being reviewed by our team.',
    amount: 'Amount',
    submitted: 'Submitted',
    pending_review: 'Pending Review',
    manual_payment_timeline: 'Manual payments are typically reviewed within 24 hours during business days.',
    
    // Manual payment submitted page
    manual_payment_submitted_title: 'Payment Proof Submitted',
    manual_payment_received: 'Payment Proof Received!',
    manual_payment_under_review: 'Your payment is under review',
    manual_payment_review_explanation: 'Our team will verify your payment and activate your subscription within 24 hours during business days.',
    payment_submitted_via: 'Payment submitted via',
    manual_payment_timeline_detailed: 'Typical review time: 4-24 hours on business days',
    manual_payment_registration_message: 'Create your account now to receive notifications about your payment status and access your subscription once approved.',
    manual_payment_benefit_1: 'Get notified when your payment is approved',
    manual_payment_benefit_2: 'Track your learning progress',
    manual_payment_benefit_3: 'Manage your subscription settings',
    manual_payment_note_1: 'You will receive email confirmation once your payment is approved',
    manual_payment_note_2: 'Your subscription will automatically activate after approval',
    manual_payment_note_3: 'Use the same email you provided for the payment when registering',
    
    // Guest checkout translations
    enter_email_to_continue: 'Enter your email to continue',
    your_email_address: 'Your email address',
    please_enter_valid_email: 'Please enter a valid email address',
    subscription_creation_error: 'Error creating subscription',
    already_have_account: 'Already have an account?',
    credit_card: 'Credit/Debit Card',
    choose_payment_method: 'Choose Payment Method',
    pay_with_credit_debit_card: 'Pay with Credit/Debit Card',
    manual_payment_label: '(Manual Payment)',
    enter_email_to_continue_checkout: 'Please enter your email address above to continue with checkout.',
    back: '← Back',
    complete_your_payment: 'Complete Your Payment',
    
    // Validation popup messages
    requirements_not_met: 'Requirements Not Met',
    please_complete_following: 'Please complete the following to continue:',
    enter_email_required: 'Enter your email address above',
    select_6_month_yearly: 'Select a 6+ month plan duration',
    manual_payments_duration_required: 'Manual payments are only available for 6+ month plans',
    paypal_not_available_usd: 'PayPal manual payment is not available for USD currency',
    continue_button: 'Continue',
    got_it: 'Got it',
    dismiss: 'Dismiss',
    
    // Enhanced email checkout
    email_checkout_options: 'Payment Options',
    email_sync_explanation: 'How it works:',
    email_sync_details: 'Use the same email to pay now and register later. Your subscription will automatically sync when you create your account.',
    prefer_register_first: 'Prefer to register first?',
    already_have_account_short: 'Have account?',
    accept_terms_prefix: 'I accept the',
    terms_and_conditions: 'Terms and Conditions',
    accept_terms_suffix: 'and agree to the subscription terms.',
    subscription_terms_notice: 'By subscribing, you accept the {TERMS_LINK}',
    subscription_terms_prefix: 'By subscribing, you accept the',
    
    // Success/Cancel pages
    processing_payment: 'Processing Payment',
    please_wait_processing: 'Please wait while we process your payment...',
    payment_successful: 'Payment Successful!',
    subscription_activated: 'Your Subscription Has Been Activated!',
    guest_checkout_success_message: 'Your payment has been processed successfully. You can now complete your registration to access all content.',
    complete_registration: 'Complete Registration',
    guest_registration_explanation: 'Create your account to access all features:',
    access_all_content: 'Access to all content',
    track_progress: 'Progress tracking',
    manage_subscription: 'Manage subscription',
    create_account: 'Create Account',
    existing_user: 'Existing User',
    existing_user_explanation: 'Already have an account with this email?',
    login_to_access: 'Login to Access',
    important_note: 'Important Note',
    email_sync_reminder: 'Use the same email you paid with so your subscription syncs automatically.',
    questions_contact_support: 'Questions? Contact us at support@profezac.com',
    back_to_home: 'Back to Home',
    subscription_success_message: 'Your subscription is now active and you can access all content.',
    go_to_dashboard: 'Go to Dashboard',
    session_id: 'Session ID',
    payment_cancelled: 'Payment Cancelled',
    subscription_not_activated: 'Your Subscription Has Not Been Activated',
    guest_checkout_cancelled_message: 'You have cancelled the payment process. No charges were applied to your payment method.',
    no_charges_applied: 'No charges were applied to your account.',
    
    // Subscription Page
    loading_pricing: 'Loading Pricing...',
    subscription_required: 'Subscription Required',
    subscription_required_message: 'Please log in to view subscription options.',
    log_in: 'Log In',
    choose_learning_plan: 'Choose Your Access Plan',
    all_plans_include: 'All plans include',
    unlock_english_potential: 'Unlock your English potential with our comprehensive video courses',
    prices_shown_in: 'Prices shown in %s for %s',
    change_currency: 'Change Currency:',
    loading_currencies: 'Loading currencies...',
    active_subscription: 'Active Subscription',
    current_access_level: 'You currently have access to: %s level content',
    expires: 'Expires: %s',
    weekly: 'Weekly',
    monthly: '1 month',
    yearly: '1 year',
    save_percentage: 'Save %s!',
    best_value: 'BEST VALUE',
    week: 'week',
    month: 'month',
    year: 'year',
    more_features: '+%s more features',
    selected_plan: 'Selected Plan',
    processing: 'Processing...',
    pay_with_card: 'Pay with Credit/Debit Card',
    mercadopago: 'MercadoPago',
    paypal: 'PayPal',
    secure_payment_cancel: 'Secure payment • Cancel anytime',
    manual_transfer_note: 'Manual Transfer: Upload payment screenshot for quick approval',
    mercadopago_yearly_only: 'MercadoPago payments are only available for yearly plans',
    bancolombia_yearly_only: 'Bancolombia payments are only available for yearly plans',
    manual_payments_yearly_only: 'Manual payments are only available for yearly plans',
    mercadopago_sixmonth_yearly_only: 'MercadoPago payments are only available for 6-month and yearly plans',
    bancolombia_sixmonth_yearly_only: 'Bancolombia payments are only available for 6-month and yearly plans',
    manual_payments_sixmonth_yearly_only: 'Manual payments are only available for 6-month and yearly plans',
    six_month_plans_only: 'Available for 6-month plans or longer',
    manual_payment_button: 'Manual Payment',
    
    // PayPal Manual Payment
    paypal_manual_payment: 'Manual Payment via PayPal',
    paypal_payment_instructions: 'Payment Instructions',
    paypal_step_1: 'Open your PayPal app or visit paypal.com',
    paypal_step_2: 'Send money to: asistente.zachmorris@gmail.com',
    paypal_step_3: 'Include your plan details in the note',
    paypal_step_4: 'Take a screenshot of the payment confirmation',
    paypal_step_5: 'Upload the screenshot below',
    paypal_send_to: 'Send payment to:',
    paypal_include_note: 'Include plan details in the note:',
    paypal_clear_screenshot: 'Take a clear screenshot of the payment confirmation',
    paypal_review_time: 'Your access will be approved in less than 24 hours',
    paypal_confirmation_screenshot: 'Payment Confirmation Screenshot',
    paypal_select_screenshot: 'Select screenshot',
    paypal_change_file: 'Change file',
    paypal_payment_summary: 'Payment Summary:',
    paypal_payment_to: 'Payment to:',
    paypal_currency: 'Currency:',
    paypal_submit_payment: 'Submit Payment',
    paypal_submitting: 'Submitting...',
    paypal_cancel: 'Cancel',
    paypal_payment_submitted: 'Payment Submitted!',
    paypal_submit_success: 'Payment submitted successfully! We will review your payment within 24 hours.',
    paypal_close: 'Close',
    paypal_upload_required: 'Please upload a screenshot of your PayPal payment confirmation',
    paypal_file_too_large: 'File is too large. Maximum 10MB.',
    paypal_invalid_file: 'Only image files (JPEG, PNG, GIF, WebP) are allowed',
    paypal_submit_error: 'Error submitting payment',
    why_choose_profezac: 'Why Choose Profezac?',
    interactive_videos: 'Interactive Videos',
    interactive_videos_desc: 'Learn with engaging, professional video content',
    smart_quizzes: 'Smart Quizzes',
    smart_quizzes_desc: 'Test your knowledge with AI-powered quizzes',
    transcriptions: 'Transcriptions',
    transcriptions_desc: 'Read along with timestamped transcripts',
    progress_tracking: 'Progress Tracking',
    progress_tracking_desc: 'Monitor your learning journey',
    back_to_dashboard: 'Back to Dashboard',
    
    // Subscription Plans
    basic_english: 'Basic English - From Zero',
    basic_english_desc: 'No prior knowledge needed. Start learning English from day one',
    advanced_english: 'Advanced English',
    advanced_english_desc: 'For intermediate learners ready to advance their skills',
    native_level: 'Native Level',
    native_level_desc: 'Master native-level English with advanced content',
    complete_access: 'Complete Access',
    complete_access_desc: 'Full access to all levels - Best Value!',
    
    // Plan Features
    access_basic_videos: 'Access to Basic level',
    basic_selling_point: 'Profe Zac has broken down the English language word by word, so that ANYONE can understand it - No prior English knowledge necessary',
    interactive_quizzes_timestamps: 'Quizzes that guarantee comprehension',
    transcriptions_after_quiz: 'Subtitles that accompany all videos',
    progress_tracking_feature: 'Progress tracking',
    learn_180_key_words: 'The 180 key words to understand most spoken English',
    videos_make_english_easy: 'Videos that make English easy to understand',
    access_any_device: 'Access from any device',
    everything_in_basic: 'Everything in Basic',
    access_advanced_videos: 'Access to Advanced level',
    advanced_selling_point: 'Building on Basic level understanding, these videos use the SAME words with less context, requiring more comprehension while still providing a safety net',
    advanced_vocabulary_grammar: 'Advanced vocabulary and grammar',
    complex_conversation_scenarios: 'Complex conversation scenarios',
    priority_support: 'Priority support',
    everything_in_advanced: 'Everything in Advanced',
    access_native_videos: 'Access to Native level',
    native_selling_point: 'The SAME words from Basic and Advanced but with NO audiovisual context - challenging exposure that accelerates your learning process dramatically',
    native_speaker_expressions: 'Native speaker expressions',
    cultural_context_idioms: 'Cultural context and idioms',
    advanced_pronunciation_training: 'Advanced pronunciation training',
    access_all_levels: 'Access to ALL levels (Basic + Advanced + Native)',
    all_levels_selling_point: 'Access to all 3 levels gives you the complete experience - from understanding English only with context to mastering it without context. Recommended for those who want to accelerate their learning',
    
    // Plan title suffixes
    advanced_title_suffix: 'Begin To Deepen',
    native_title_suffix: 'Mastery',
    all_levels_title_suffix: 'Complete Learning',
    
    unlimited_video_access: 'Unlimited video access',
    unlimited_quiz_attempts: 'Unlimited quiz attempts',
    all_transcriptions_included: 'All transcriptions included',
    new_content_released: 'New content as it\'s released',
    
    // Duration Labels
    per_week: 'per week',
    per_month: 'per month',
    per_year: 'per year',
    
    // Additional Subscription Page Labels
    select_plan: 'Please select a subscription plan',
    pricing_load_error: 'Unable to load pricing information. Please refresh the page.',
    currency_change_error: 'Unable to change currency. Please try again.',
    
    // Register Form Validation
    passwords_do_not_match: 'Passwords do not match',
    password_too_short: 'Password must be at least 6 characters',
    
    // Daily Inspiration
    daily_inspiration_title: 'Message of the Day from Profe Zac',
    daily_inspiration_loading: 'Loading today\'s inspiration...',
    daily_inspiration_error: 'Unable to load daily message',
    daily_inspiration_try_again: 'Try Again',
    daily_inspiration_refresh: 'Refresh',
    daily_inspiration_video_message: 'Daily video message',
    daily_inspiration_text_message: 'Daily inspiration text',
    daily_inspiration_today_goal: 'Today\'s Goal',
    daily_inspiration_today_goal_text: 'Practice one new English phrase or conversation today!',
    daily_inspiration_remember: 'Remember',
    daily_inspiration_remember_text: 'Every step forward is progress. ¡Tú puedes!',
    daily_inspiration_personalized: 'Personalized recommendation',
    daily_inspiration_new_lesson: 'New lesson for you!',
    daily_inspiration_continue_progress: 'Continue your progress!',
    daily_inspiration_go_to_lesson: 'Go to full lesson',
    daily_inspiration_show_inspiration: 'Daily inspiration',
    
    // Home page join section
    ready_to_start: 'Ready to Start Learning?',
    join_now: 'JOIN NOW',
    home_join_description: 'Start learning one English word per day and make English easier every day. This is the only course made by a native speaker - designed to teach you the most common words in English.',
    
    // Preview overlay translations
    preview_unlock_text: 'Unlock access to this video and 180+ more lessons with interactive quizzes',
    get_full_access: 'Get Full Access',
    subscription_modal_text: 'Join thousands mastering English with our complete course!',
    subscription_videos_count: '180+ videos',
    subscription_interactive_quizzes: 'Interactive quizzes',
    subscription_all_levels: 'All levels',
    start_learning_today: 'Start Learning Today',
    ready_for_more: 'Ready for more?',
    sample_lesson_text: 'Sample English Lesson',
    shuffle_video: 'Next Video',
    
    // Membership Sync
    sync_paypal: 'Sync Membership',
    sync_membership: 'Sync Membership',
    sync_paypal_membership: 'Link Your Subscription',
    link_paypal_subscription: 'Link Your Subscription',
    membership_sync_desc: 'If you have an active subscription with PayPal, MercadoPago, etc. that isn\'t showing in your account, use this form to link it. Our team will verify your payment and grant access.',
    subscription_details_optional: 'Subscription Details (Optional)',
    subscription_id_label: 'Subscription ID',
    paypal_subscription_id: 'PayPal Subscription ID',
    subscription_id_placeholder: 'I-XXXXXXXXX (if you have it)',
    subscription_id_help: 'This can be found in your PayPal account under "Automatic Payments"',
    payment_information: 'Payment Information',
    amount_paid: 'Amount Paid',
    currency: 'Currency',
    duration: 'Duration',
    course_access_level: 'Course Access Level',
    payment_proof_optional: 'Payment Proof (Optional)',
    upload_payment_proof: 'Upload a screenshot of your payment or receipt (PNG, JPG, PDF - max 5MB)',
    additional_notes: 'Additional Notes',
    additional_notes_placeholder: 'Any additional information about your subscription...',
    review_process: 'Review Process:',
    review_process_desc: 'Our team will review your request within 1-2 business days. You\'ll receive an email notification once approved.',
    usd_dollar: 'USD - US Dollar',
    eur_euro: 'EUR - Euro',
    ars_peso: 'ARS - Argentine Peso',
    cop_peso: 'COP - Colombian Peso',
    mxn_peso: 'MXN - Mexican Peso',
    brl_real: 'BRL - Brazilian Real',
    gbp_pound: 'GBP - British Pound',
    cad_dollar: 'CAD - Canadian Dollar',
    submit_request: 'Submit Request',
    submitting: 'Submitting...',
    cancel: 'Cancel',
    your_previous_requests: 'Your Previous Requests',
    amount: 'Amount',
    level: 'Level',
    status: 'Status',
    submitted: 'Submitted',
    pending_review: 'Pending Review',
    approved: 'Approved',
    denied: 'Denied',
    under_review: 'Under Review',
    request_submitted_success: '✅ Membership sync request submitted successfully! Our team will review it within 1-2 business days.',
    monthly: '1 month',
    yearly: '1 year',
    semiannual: '6 Months',
    lifetime: 'Lifetime',
    basic_level: 'Basic Level',
    advanced_level: 'Advanced Level',
    native_level: 'Native Level',
    all_levels: 'All Levels',
    
    // Terms and Conditions
    terms_title: 'Terms of Service',
    terms_intro: 'Please read these terms carefully before using our service.',
    terms_section_1_title: '1. Immediate Course Access',
    terms_section_1_p1: 'Once you make your purchase, you will have immediate access via the portal.',
    terms_section_1_p2: 'All course content is available through the portal platform.',
    terms_section_2_title: '2. Subscription Plans and Cancellation',
    terms_section_2_p1: 'We offer plans for one week, one month, and one year. Once you have acquired the plan, access is granted during the period that is purchased.',
    terms_section_2_p2: 'Your membership will be active from the moment in which you purchased - that is to say, from the moment which you have access to the course, your purchase period is active.',
    terms_section_2_p3: 'To cancel your subscription, just go to the dashboard and within the user dashboard you\'ll find the section where you manage your subscription.',
    terms_section_2_p4: 'Even though you\'ve canceled your membership, you\'ll still have access until the end of the period for which your plan was purchased.',
    terms_section_2_p5: 'It is your responsibility to cancel your subscription before the renewal date to avoid being charged automatically for the next billing cycle.',
    terms_section_3_title: '3. No Refund Policy',
    terms_section_3_p1: 'Due to the high value and accessibility of our service, we do not offer refunds or returns.',
    terms_section_3_p2: 'We recommend acquiring the weekly or monthly plan initially to evaluate the course before opting for more expensive plans.',
    terms_section_3_p3: 'By purchasing the course, you confirm that you understand that all sales are final and no refunds will be issued.',
    terms_section_3_p4: 'Disputing a legitimate charge could result in the suspension or termination of your account.',
    terms_section_4_title: '4. Payment Methods',
    terms_section_4_p1: 'Accepted payment methods include credit/debit cards, PayPal, Bitcoin, Mercado Pago, and Bancolombia.',
    terms_section_4_p2: 'Local payment methods are also offered in some countries.',
    terms_section_5_title: '5. Content Access and Ownership',
    terms_section_5_p1: 'When you purchase a subscription plan, you are paying for access to our educational content, including videos, quizzes, and transcriptions. This subscription grants you the right to access and use the course materials during your active subscription period.',
    terms_section_5_p2: 'Please note that the course content, including all videos, quizzes, transcriptions, and related materials, remain the intellectual property of English with Profezac. Your subscription does not transfer ownership of any content to you, and you may not redistribute, copy, or share the course materials with others.',
    terms_section_6_title: '6. Discounts and Promotions',
    terms_section_6_content: 'Placeholder for section 6 content - to be updated.',
    terms_section_7_title: '7. Learning Philosophy',
    terms_section_7_content: 'Placeholder for section 7 content - to be updated.',
    terms_section_8_title: '8. Privacy and Data Security',
    terms_section_8_content: 'Placeholder for section 8 content - to be updated.',
    terms_section_9_title: '9. Terms Acceptance',
    terms_section_9_p1: 'By purchasing and accessing the course, you acknowledge and accept these Terms of Service.',
    terms_section_9_p2: 'These terms are subject to change, and updates will be published on our official website.',
    
    // BasicLevelLanding translations
    basic_english: 'Basic English',
    learn_180_key_words: 'Learn the 180 key words for understanding most spoken English',
    videos_make_english_easy: 'Videos that make English easy',
    basic_english_desc: 'English from zero to understanding basic conversations',
    monthly: 'month',
    free_no_commitment: 'No commitment',
    start_learning_today: 'Start learning today',
    how_basic_level_works: 'How Basic Level Works',
    basic_level_description: 'Start your English journey with essential vocabulary and pronunciation',
    watch_basic_demo: 'Watch & Learn',
    basic_description: 'Watch Profe Zac explain English using real-world contexts you understand',
    practice_quiz: 'Practice & Quiz',
    test_understanding_quizzes: 'Test your understanding with interactive quizzes after each lesson',
    simple_vocabulary: 'Build Vocabulary',
    home_get_started_free: 'Get Started Now',
    profe_confidence_message: 'Profe Zac doesn\'t just teach words - he shows you how English works in real situations',
    basketball_basics_desc: 'Real context, not boring textbook examples',
    subtitles_connect_sounds: 'Subtitles to help you connect sounds with words',
    try_real_basic_quiz: 'Try a Real Basic Level Quiz',
    start_english_journey_today: 'Start Your English Journey Today',
    access_all_basic_videos: 'Access to all Basic Level videos',
    interactive_quizzes_feedback: 'Interactive quizzes with instant feedback',
    progress_tracking_improvement: 'Progress tracking to see your improvement',
    subtitles_transcriptions_included: 'Subtitles and transcriptions included',
    start_course: 'Start Course',
    learn_english_from_zero_with_profe_zac: 'Learn English from Zero with Profe Zac',
    about_to_start_conversations: 'You are about to start understanding conversations in English !',
    want_to_learn_english_from_zero: 'I want to learn English from zero',
    motivational_text_base: 'If you haven\'t been able to learn English until now, it\'s not because you can\'t, it\'s because no one has taught you the right way. And this is where English with Profe Zac comes in. This course was created with one purpose: to make learning English easy, accessible and effective for',
    latino_community_default: 'the Latino community',
    past_attempts_title: 'Have you tried to learn English unsuccessfully in the past?',
    past_attempts_subtitle: 'Maybe you relate to these common experiences:',
    past_attempt_1: '"I started many courses but never finished them."',
    past_attempt_2: '"I feel like I\'m not making real progress."',
    past_attempt_3: '"Traditional methods are boring and confusing."',
    past_attempt_4: '"I can\'t find time to study consistently."',
    past_attempt_5: '"I feel embarrassed about my pronunciation."',
    encouragement_title: 'Here\'s the thing...',
    encouragement_message: 'You\'re so much closer to understanding English conversations than you think!',
    cancel_anytime: 'Cancel anytime',
    start_immediately: 'Start immediately',
    ready_learn_english_zero: 'Ready to Learn English from Zero?',
    join_thousands_students: 'Join thousands of students who started with no English and now understand conversations',
    get_started_today: 'Get Started Today',
    
    // Quiz instructions
    quiz_instruction_distractor: 'Find the correct words and arrange them in the correct order to match what you heard in the video:'
  },
  
  [LANGUAGES.ES]: {
    // Navegación
    nav_home: 'Inicio',
    nav_dashboard: 'Panel Principal',
    nav_login: 'Iniciar Sesión',
    nav_register: 'Registrarse',
    nav_logout: 'Cerrar Sesión',
    nav_signup: 'Registrarse',
    nav_account: 'Cuenta',
    nav_levels: 'Niveles',
    nav_access_requests: 'Solicitudes de Acceso',
    courses: 'Cursos',
    site_title: 'Inglés con Profezac',
    
    // Niveles
    level_basic: 'Básico',
    level_advanced: 'Avanzado',
    level_native: 'Nativo',
    level_all: 'Todos los Niveles',
    
    // Página de inicio
    home_title: 'Domina el Inglés a Tu Propio Ritmo',
    home_subtitle: 'Únete a nuestro curso completo de inglés con lecciones diarias en video en niveles Básico, Avanzado y Nativo. Mejora tus habilidades con cuestionarios interactivos y sigue tu progreso.',
    home_get_started_free: 'Comenzar Gratis',
    home_login: 'Iniciar Sesión',
    home_try_demos: 'O prueba nuestras demostraciones gratuitas de video:',
    home_demo_title: 'Prueba Nuestras Demostraciones Gratuitas de Video y Quiz',
    home_demo_subtitle: 'Experimenta cada nivel con nuestras muestras de lecciones de baloncesto',
    not_ready_sign_up: '(¿aún no estás listo para registrarte?)',
    
    // Tarjetas de nivel
    basic_level: 'Nivel Básico',
    advanced_level: 'Nivel Avanzado',
    native_level: 'Nivel Nativo',
    basketball_basics: 'Fundamentos de Baloncesto',
    basketball_techniques: 'Técnicas de Baloncesto',
    basketball_stories: 'Historias de Baloncesto',
    basketball_basics_desc: 'Aprende vocabulario simple: "Esto es un baloncesto. Dribo el baloncesto."',
    basketball_techniques_desc: '"Esto es una bandeja. Cuando fallas y agarras la pelota, eso es un rebote."',
    basketball_stories_desc: '"Me cortaron del equipo de la secundaria. El baloncesto favorece la altura..."',
    
    // Listas de características
    simple_vocabulary: 'Vocabulario simple',
    clear_pronunciation: 'Pronunciación clara',
    basic_sentence_patterns: 'Patrones básicos de oraciones',
    technical_vocabulary: 'Vocabulario técnico',
    detailed_explanations: 'Explicaciones detalladas',
    complex_sentences: 'Oraciones complejas',
    natural_conversation: 'Conversación natural',
    personal_stories: 'Historias personales',
    cultural_context: 'Contexto cultural',
    
    // Botones de demostración
    watch_basic_demo: 'Ver Demostración Básica',
    watch_advanced_demo: 'Ver Demostración Avanzada',
    watch_native_demo: 'Ver Demostración Nativa',
    
    // Descripción de niveles
    basic_description: 'Perfecto para principiantes. Aprende vocabulario y gramática esenciales para comenzar tu viaje en inglés.',
    advanced_description: 'Para estudiantes intermedios. Mejora tu fluidez con estructuras y vocabulario más complejos.',
    native_description: 'Domina los matices del inglés con modismos, referencias culturales y habilidades de comunicación avanzadas.',
    learn_more: 'Saber Más',

    // Panel Principal
    dashboard_title: 'Panel Principal',
    welcome: 'Bienvenido',
    dashboard_intro: 'Accede a tu contenido de aprendizaje de inglés aquí.',
    subscription_status: 'Estado de Suscripción',
    subscription_active_message: '¡Tu suscripción está activa!',
    access_status_message: '¡Tienes acceso!',
    subscription_status_active: 'Activa',
    subscription_status_cancelled: 'Cancelada',
    subscription_cancelled_notice: 'Tu suscripción ha sido cancelada. Mantienes acceso hasta la fecha de expiración.',
    subscription_reactivate: 'Reactivar',
    subscription_reactivating: 'Reactivando...',
    subscription_error_reactivate: 'Error al reactivar la suscripción. Inténtalo de nuevo.',
    reactivate_current_subscription: 'Reactivar Suscripción Actual',
    get_new_subscription: 'Obtener Nueva Suscripción',
    change_plan: 'Cambiar Plan',
    subscription_inactive_message: 'No tienes una suscripción activa.',
    subscription_level: 'Nivel',
    subscription_level_basic: 'Básico',
    subscription_level_advanced: 'Avanzado',
    subscription_level_native: 'Nativo',
    subscription_level_all: 'Todos los Niveles',
    subscription_expires: 'Expira',
    payment_method: 'Método de Pago',
    manage_subscription: 'Gestionar Suscripción',
    get_subscription: 'Obtener Suscripción',
    start_secure_checkout: 'Iniciar Pago Seguro',
    renew_subscription: 'Renovar Suscripción',
    reactivate_subscription: 'Reactivar Suscripción',
    subscription_cancelled: 'Suscripción Cancelada',
    subscription_cancelled_message: 'Aún tienes acceso hasta',
    cancelled_on: 'Cancelada el',
    access_until: 'Acceso hasta',
    subscription_expired_message: 'Tu suscripción expiró el',
    previous_payment_method: 'Método de Pago Anterior',
    learning_levels: 'Niveles de Aprendizaje',
    learn_about_levels: 'Aprende Sobre los Niveles',
    level_basic_desc: 'Inglés fundamental para principiantes.',
    level_advanced_desc: 'Práctica de inglés intermedio a avanzado.',
    level_native_desc: 'Contenido auténtico de hablante nativo.',
    explore_level: 'Explorar Nivel',
    free_preview: 'Prueba lecciones gratis',
    unlock_all: 'Desbloquear Todo',
    
    // Propuesta de valor gratuito
    profe_confidence_message: '¡El Profe Zac está tan seguro de que te encantará aprender inglés con sus videos que te está regalando acceso gratuito (un valor de $8)!',
    free_access_includes: 'Tu acceso GRATUITO incluye:',
    free_video_lessons: '3 Lecciones en Video + Cuestionarios Interactivos',
    free_all_levels: '(Niveles Básico, Avanzado y Nativo)',
    free_daily_inspiration: 'Videos de Inspiración Diarios',
    free_progress_tracking: 'Panel de Seguimiento de Progreso',
    free_no_payment: '¡Sin pago requerido - Comienza inmediatamente!',
    free_no_commitment: 'Sin compromiso ninguno',
    free_value_amount: 'Valor $8 - Tuyo GRATIS',
    
    // Contenido freemium
    freemium_header: 'Vista Previa Gratuita',
    freemium_subtitle: 'videos de muestra',
    freemium_viewing_message: 'Estás viendo',
    freemium_from_collection: 'de nuestra colección de nivel',
    freemium_level_collection: '',
    freemium_upgrade_message: '¡Actualiza para acceder a todos los',
    freemium_videos_and_quizzes: 'videos y cuestionarios ilimitados!',
    freemium_upgrade_now: 'Actualizar Ahora',
    freemium_quiz_attempts: 'Intentos de Cuestionario',
    freemium_quiz_limit_reached: 'Límite de Cuestionario Alcanzado',
    freemium_available_videos: 'Videos Disponibles',
    freemium_want_access: '¿Quieres acceso a todos los',
    freemium_unlock_videos: 'Desbloquear Todos los Videos',
    freemium_select_video: 'Selecciona un video para comenzar a aprender',
    freemium_choose_from: 'Elige entre los',
    freemium_available_videos_text: 'videos freemium disponibles',
    freemium_interactive_quiz: 'Cuestionario Interactivo',
    freemium_attempts_used: 'intentos utilizados',
    freemium_quiz_limit_message: 'Has usado todos tus intentos gratuitos de cuestionario.',
    freemium_upgrade_unlimited: 'Actualizar para Cuestionarios Ilimitados',
    loading_freemium_content: 'Cargando contenido freemium...',
    loading_quiz: 'Cargando cuestionario...',
    
    // Dashboard course access
    course_access: 'Acceso al Curso',
    complete_english_course: 'Curso Completo de Inglés',
    course_access_description: 'Accede a las 180 palabras esenciales del inglés organizadas por nivel.\nDomina el vocabulario con las lecciones en video integrales del Profesor Zac.',
    total_words: 'Palabras Totales',
    skill_levels: 'Niveles de Habilidad',
    practice_quizzes: 'Cuestionarios de Práctica',
    start_learning_now: 'Comenzar a Aprender Ahora',
    full_access_all_levels: '¡Tienes acceso completo a todos los niveles!',
    access_basic_advanced_native: 'Acceso a Básico, Avanzado y Nativo',
    access_basic_advanced: 'Acceso a Básico y Avanzado',
    access_basic_level: 'Acceso al nivel Básico',
    signup_start_journey: 'Regístrate para comenzar tu viaje en inglés',
    
    // Video card descriptions - fallback text
    learn_word_with_professor: 'Aprende esta palabra con el Profesor Zac',
    
    // Dynamic description translations
    learn_the_activity: 'Aprende la actividad',
    learn_conversation_about: 'Aprende conversación sobre',
    learn_the_verb: 'Aprende el verbo',
    learn_the_noun: 'Aprende el sustantivo',
    learn_the_adjective: 'Aprende el adjetivo',
    learn_the_adverb: 'Aprende el adverbio',
    learn_the_preposition: 'Aprende la preposición',
    learn_the_word: 'Aprende el/la',
    in_level_english: 'en inglés nivel %s',
    
    // Archive page text
    all_words_complete_archive: 'Todas las 180 Palabras - Archivo Completo',
    browse_all_english_words: 'Explora todas las palabras en inglés de todos los niveles',
    back_to_dashboard: 'Volver al Tablero',
    search_words_placeholder: 'Buscar palabras...',
    your_access_level: 'Tu Nivel de Acceso',
    all_progress: 'Todo el Progreso',
    not_started: 'Sin Comenzar',
    in_progress: 'En Progreso',
    completed: 'Completado',
    words_found: 'Palabras Encontradas',
    no_words_found: 'No se encontraron palabras',
    try_adjusting_filters: 'Intenta ajustar tu búsqueda o criterios de filtro',
    watch: 'Ver',
    upgrade: 'Actualizar',
    upgrade_for_full_access: 'Actualizar para Acceso Completo',
    filter_by_progress: 'Filtrar por Progreso:',
    quiz_error: 'Error de Cuestionario',
    your_subscription: 'Tu suscripción',
    today_lessons: 'Lecciones de Hoy',
    today_video: 'Lección en Video de Hoy',
    watch_video: 'Ver Video',
    video_archives: 'Archivo de Videos',
    access_previous: 'Accede a Lecciones Anteriores',
    browse_archive: 'Explora nuestro archivo de lecciones en video por nivel.',
    
    // Gestión de Nivel de Acceso
    access_level_management: 'Gestión de Nivel de Acceso',
    request_access_change: 'Pedir Cambio de Nivel',
    cancel_request: 'Cancelar Solicitud',
    view_my_requests: 'Ver Mis Solicitudes',
    hide_my_requests: 'Ocultar',
    your_access_change_requests: 'Tus Solicitudes de Cambio de Acceso',
    no_access_requests: 'No se encontraron solicitudes de cambio de acceso.',
    request_submitted_success: '¡Tu solicitud de cambio de acceso ha sido enviada exitosamente! Un administrador la revisará pronto.',
    request_submit_failed: 'Error al enviar la solicitud',
    network_error: 'Error de red. Por favor intenta de nuevo.',
    request_access_change_form: 'Solicitar Cambio de Acceso',
    requested_access_level: 'Nivel de Acceso Solicitado',
    select_level: 'Selecciona un nivel...',
    reason_for_request: 'Razón de la Solicitud',
    reason_placeholder: 'Por favor explica por qué necesitas este cambio de nivel de acceso. Incluye detalles sobre tu suscripción o cualquier problema que estés experimentando...',
    characters: 'caracteres',
    
    // New adaptive translations
    request_access: 'Solicitar Acceso',
    change_access_level: 'Cambiar Nivel de Acceso',
    no_active_access_message: 'Actualmente no tienes acceso activo. ¡Solicita acceso para comenzar a aprender!',
    request_access_form: 'Solicitar Acceso',
    request_access_help_text: 'Si crees que deberías tener acceso o estás experimentando problemas con tu suscripción, envía una solicitud a continuación.',
    request_access_reason_placeholder: 'Por favor explica por qué necesitas acceso. Por ejemplo: "Pagué pero no veo mi suscripción" o "Mi suscripción expiró pero quiero seguir aprendiendo"...',
    your_subscription_info: 'Información de Tu Suscripción',
    current_level: 'Nivel Actual',
    other_levels: 'Otros Niveles',
    view_level: 'Ver Nivel %s',
    upgrade_required_level: 'Actualización Requerida para %s',
    access_denied_level: 'Acceso denegado para el nivel %s. Por favor actualiza tu suscripción.',
    access_denied_title: 'Acceso Denegado',
    access_denied_message: 'No tienes acceso al nivel %s.',
    upgrade_to_access_message: 'Actualiza tu membresía para desbloquear todos los niveles y continuar aprendiendo.',
    upgrade_membership: 'Actualizar Membresía',
    back: 'Atrás',
    cancel: 'Cancelar',
    submit_request: 'Enviar Solicitud',
    submitting: 'Enviando...',
    requested: 'Solicitado',
    reason: 'Razón',
    admin_notes: 'Notas del Administrador',
    basic_level_desc_short: 'Contenido fundamental de aprendizaje de inglés',
    advanced_level_desc_short: 'Contenido de inglés más desafiante',
    native_level_desc_short: 'Contenido de nivel de hablante nativo',
    all_levels_desc: 'Acceso a todos los niveles de contenido',
    
    // Autenticación
    email_address: 'Correo Electrónico',
    password: 'Contraseña',
    confirm_password: 'Confirmar Contraseña',
    first_name: 'Nombre',
    last_name: 'Apellido',
    already_account: '¿Ya tienes una cuenta?',
    no_account: '¿No tienes una cuenta?',
    register_here: 'Regístrate aquí',
    login_here: 'Inicia sesión aquí',
    forgot_password: '¿Olvidaste tu contraseña?',
    logging_in: 'Iniciando sesión...',
    registering: 'Registrando...',
    
    // Videos
    back_to_dashboard: 'Volver al Panel Principal',
    video_of_day: 'Lección de Inglés %s de Hoy',
    description: 'Descripción',
    published_on: 'Publicado el',
    view_archive: 'Ver Archivo',
    browse_archive_level: 'Explorar Archivo %s',
    no_video: 'No hay video disponible',
    error_loading: 'Error al cargar el video',
    try_again: 'Intentar de Nuevo',
    about_lesson: 'Acerca de esta lección',
    learn_word: 'Aprende "%s" en inglés nivel %s',
    word_type_noun: 'el sustantivo',
    word_type_verb: 'el verbo', 
    word_type_adjective: 'el adjetivo',
    word_type_preposition: 'la preposición',
    word_type_adverb: 'el adverbio',
    
    // Transcripción
    transcription_locked: 'Transcripción Bloqueada',
    complete_quiz_unlock: 'Completa el cuestionario para este video para desbloquear la transcripción',
    
    // Cuestionario (Quiz)
    take_quiz: 'Hacer Cuestionario',
    quiz_results: 'Resultados del Cuestionario',
    test_understanding: 'Pon a Prueba Tu Comprensión',
    submit_quiz: 'Enviar Cuestionario',
    next_question: 'Siguiente Pregunta',
    previous_question: 'Pregunta Anterior',
    hide_quiz: 'Ocultar Cuestionario',
    generate_quiz: 'Generar Cuestionario',
    loading_quiz: 'Cargando Cuestionario...',
    generating_quiz: 'Generando Cuestionario...',
    watch_segment: 'Ver Segmento',
    pause_segment: 'Pausar Segmento',
    press_again_if_cut_short: '💡 Presiona de nuevo si se cortó',
    
    // Quiz instructions  
    arrange_words_instruction: 'Ordena estas palabras en el orden correcto para que coincidan con lo que escuchaste en el video:',
    select_correct_answers_instruction: 'Basándote en lo que viste en el video, selecciona todas las respuestas correctas:',
    
    your_score: 'Tu Puntuación',
    answered_questions: 'Has respondido correctamente %s de %s preguntas.',
    question_review: 'Revisión de Preguntas',
    question: 'Pregunta',
    of: 'de',
    correct: 'Correcto',
    incorrect: 'Incorrecto',
    your_answer: 'Tu respuesta',
    correct_answer: 'Respuesta correcta',
    explanation: 'Explicación',
    no_answer: '[Sin respuesta]',
    error_during_submission: 'Error durante el envío',
    back_to_video: 'Volver al Video',
    quiz_not_found: 'Cuestionario No Encontrado',
    quiz_not_found_message: 'El cuestionario solicitado no pudo ser encontrado o cargado.',
    loading_question: 'Cargando pregunta...',
    go_to_question: 'Ir a la pregunta',
    error: 'Error',
    submitting: 'Enviando...',
    
    // Estado de suscripción
    active: 'activa',
    expired: 'vencida',
    cancelled: 'cancelada',
    pending: 'pendiente',
    
    // Footer
    footer_rights: 'Todos los Derechos Reservados',
    footer_privacy: 'Política de Privacidad',
    footer_terms: 'Términos de Uso',
    footer_contact: 'Contáctanos',
    
    // VideoArchive
    filter_by_type: 'Filtrar por tipo',
    all_types: 'Todos los Tipos',
    showing_videos: 'Mostrando %s de %s videos',
    no_videos_found: 'No se encontraron videos para este nivel y tipo.',
    general: 'General',
    previous: 'Anterior',
    next: 'Siguiente',
    
    // Página de Explicación de Niveles
    levels_explained_title: 'Domina el Inglés a Tu Propio Ritmo',
    levels_explained_subtitle: 'Únete a nuestro curso completo de inglés con lecciones diarias en video en niveles Básico, Avanzado y Nativo. Mejora tus habilidades con cuestionarios interactivos y sigue tu progreso.',
    basketball_example_intro: 'Ejemplo: Aprendiendo "Basketball"',
    basketball_example_desc: 'Ve cómo se enseña el mismo tema en los tres niveles. Cada nivel se basa en el anterior, proporcionando la progresión perfecta para tu viaje de aprendizaje de inglés.',
    what_youll_learn: 'Lo que Aprenderás:',
    basketball_example: 'Ejemplo de Basketball:',
    perfect_for: 'Perfecto para:',
    watch_sample_video: 'Ver Video de Muestra',
    free_demo_signup: 'Demo gratuita - Regístrate para acceder al contenido completo',
    how_learning_works: 'Cómo Funciona Nuestro Sistema de Aprendizaje',
    watch_videos: 'Ver Videos',
    watch_videos_desc: 'Ve lecciones diarias en video adaptadas a tu nivel con explicaciones claras y ejemplos del mundo real.',
    take_quizzes: 'Hacer Cuestionarios',
    take_quizzes_desc: 'Completa cuestionarios interactivos que se adaptan a tu nivel y ayudan a reforzar lo que has aprendido.',
    track_progress: 'Seguir Progreso',
    track_progress_desc: 'Monitorea tu mejora y desbloquea transcripciones mientras dominas cada lección.',
    ready_to_start: '¿Listo para Comenzar a Aprender?',
    ready_to_start_desc: 'Únete a miles de estudiantes mejorando su inglés con nuestro método probado. ¡Elige tu nivel y comienza tu viaje hoy!',
    sign_up_free: 'Registrarse Gratis',
    login: 'Iniciar Sesión',
    faq_title: 'Preguntas Frecuentes',
    faq_which_level: '¿Con qué nivel debo comenzar?',
    faq_which_level_answer: 'Comienza con Básico si eres nuevo en inglés, Avanzado si puedes tener conversaciones simples, o Nativo si quieres perfeccionar tu fluidez con contenido auténtico.',
    faq_multiple_levels: '¿Puedo acceder a múltiples niveles?',
    faq_multiple_levels_answer: '¡Sí! Nuestros planes de suscripción permiten acceso a tu nivel elegido y todos los niveles inferiores. El plan "Todos los Niveles" te da acceso completo a todo el contenido.',
    faq_how_quizzes: '¿Cómo funcionan los cuestionarios?',
    faq_how_quizzes_answer: 'Después de ver un video, tomarás un cuestionario interactivo basado en el contenido. Aprueba el cuestionario para desbloquear la transcripción del video y seguir tu progreso.',
    
    // Contenido específico de niveles
    basic_level_title: 'Nivel Básico',
    basic_level_description: 'Perfecto para principiantes que aprenden vocabulario fundamental de inglés y estructuras de oraciones simples.',
    basic_features_1: 'Introducción de vocabulario simple',
    basic_features_2: 'Pronunciación clara',
    basic_features_3: 'Patrones básicos de oraciones',
    basic_features_4: 'Repetición para el aprendizaje',
    basic_example_text: 'This is a BASKETBALL. A BASKETBALL. This is a BASKETBALL COURT. I DRIBBLE the BASKETBALL. I SHOOT the BASKETBALL.',
    basic_audience: 'Principiantes con poca o ninguna experiencia en inglés',
    
    advanced_level_title: 'Nivel Avanzado',
    advanced_level_description: 'Para estudiantes intermedios listos para expandir vocabulario y entender conceptos más complejos.',
    advanced_features_1: 'Vocabulario avanzado',
    advanced_features_2: 'Explicaciones detalladas',
    advanced_features_3: 'Estructuras de oraciones complejas',
    advanced_features_4: 'Aprendizaje condicional y contextual',
    advanced_example_text: 'This is a layup. This is a rebound. When you shoot and you miss and you catch the ball, that\'s a rebound. A jump shot is when you jump and you shoot.',
    advanced_audience: 'Estudiantes intermedios cómodos con inglés básico',
    
    native_level_title: 'Nivel Nativo',
    native_level_description: 'Contenido auténtico de hablante nativo con conversación natural, modismos y contexto cultural.',
    native_features_1: 'Flujo de conversación natural',
    native_features_2: 'Experiencias personales e historias',
    native_features_3: 'Contexto cultural y matices',
    native_features_4: 'Gramática y expresiones complejas',
    native_example_text: 'I really enjoy basketball because when you play basketball, you don\'t have to think about anything else. Basketball is a sport that favors height. I got cut from the high school team.',
    native_audience: 'Estudiantes avanzados que buscan fluidez de nivel nativo',
    
    // Página de Demo del Curso
    course_demo_title: 'Demo de %s',
    course_demo_subtitle: 'Experimenta nuestro contenido de nivel %s con esta lección real de baloncesto',
    basketball_level_title: 'Baloncesto - %s',
    sample_content: 'Contenido de Muestra:',
    try_interactive_quiz: 'Probar el Cuestionario Interactivo',
    interactive_quiz_title: 'Cuestionario Interactivo: %s',
    interactive_quiz_preview: 'Vista Previa del Cuestionario Interactivo',
    quiz_preview_intro: 'Después de ver el video, tomarás un cuestionario con preguntas como:',
    sample_question: 'Pregunta de Muestra:',
    complete_quiz_unlock_transcript: '¡Completa el cuestionario para desbloquear la transcripción completa con marcas de tiempo palabra por palabra!',
    what_youll_learn_level: 'Lo que Aprenderás en %s',
    ready_to_start_learning: '¿Listo para Comenzar a Aprender?',
    course_demo_cta_text: '¡Esto es solo una vista previa! Regístrate para acceder a cientos de videos en todos los niveles.',
    compare_all_levels: 'Comparar todos los niveles',
    loading_demo_content: 'Cargando contenido de demostración...',
    error_loading_demo: 'Error al Cargar la Demostración',
    try_again: 'Intentar de Nuevo',
    level_not_found: 'Nivel no encontrado',
    real_video_streaming: 'Transmisión de video real disponible en la versión completa',
    demo_mode_notice: 'Modo demo: Se requiere conexión a la base de datos para el video real',
    fully_functional_demo: '¡Esta es una demostración completamente funcional! En la versión completa, completar cuestionarios desbloquea transcripciones con marcas de tiempo palabra por palabra.',
    
    // Descripciones de Niveles del Demo del Curso
    basic_video_description: 'En esta lección de baloncesto, aprenderás vocabulario básico como \'basketball\', \'court\', y \'goal\'. El profesor habla lenta y claramente, repitiendo cada palabra varias veces para ayudarte a memorizar y pronunciar correctamente.',
    advanced_video_description: 'Esta lección avanzada de baloncesto introduce términos técnicos como \'layup\', \'rebound\', y \'jump shot\'. Aprenderás explicaciones y condiciones (cuando pasa X, entonces Y) para entender la mecánica del baloncesto.',
    native_video_description: 'Experimenta habla nativa auténtica sobre baloncesto. El profesor comparte historias personales sobre ser cortado del equipo de la secundaria y discute cómo la altura afecta el rendimiento en baloncesto - perspectivas culturales reales que no encontrarás en los libros de texto.',
    
    // Preguntas del Cuestionario - Nivel Básico
    basic_quiz_question_1: 'Ordena estas palabras en el orden correcto:',
    basic_quiz_question_2: '¿Qué haces con un baloncesto?',
    basic_quiz_answer_dribble: 'Driblар',
    basic_quiz_answer_eat: 'Comer',
    basic_quiz_answer_read: 'Leer',
    basic_quiz_answer_sleep: 'Dormir',
    
    // Preguntas del Cuestionario - Nivel Avanzado
    advanced_quiz_question_1: '¿Qué es un rebote en baloncesto?',
    advanced_quiz_answer_1_correct: 'Cuando agarras la pelota después de un tiro fallado',
    advanced_quiz_answer_1_wrong_1: 'Cuando anotas puntos',
    advanced_quiz_answer_1_wrong_2: 'Cuando pasas la pelota',
    advanced_quiz_answer_1_wrong_3: 'Cuando empiezas el juego',
    advanced_quiz_question_2: 'Un tiro con salto es cuando [ESPACIO 1] y [ESPACIO 2].',
    
    // Preguntas del Cuestionario - Nivel Nativo
    native_quiz_question_1: '¿Qué significa "got cut from the team"?',
    native_quiz_answer_1_correct: 'Fue removido del equipo',
    native_quiz_answer_1_wrong_1: 'Se lesionó jugando',
    native_quiz_answer_1_wrong_2: 'Ganó el campeonato',
    native_quiz_answer_1_wrong_3: 'Se convirtió en capitán',
    native_quiz_question_2: '¿Por qué dice el hablante que el baloncesto "favorece la altura"?',
    native_quiz_answer_2_correct: 'Los jugadores más altos tienen ventajas',
    native_quiz_answer_2_wrong_1: 'Las canchas de baloncesto son altas',
    native_quiz_answer_2_wrong_2: 'La pelota es pesada',
    native_quiz_answer_2_wrong_3: 'El juego es difícil',
    
    // Página de Gestión de Suscripción
    subscription_title: 'Gestión de Suscripción',
    subscription_login_required: 'Inicio de Sesión Requerido',
    subscription_login_message: 'Necesitas iniciar sesión para gestionar tu suscripción',
    subscription_error_redirect: 'Error al redirigir al portal de pagos',
    subscription_confirm_cancel: '¿Estás seguro de que quieres cancelar tu suscripción?',
    subscription_motivational_title: 'Antes de Cancelar - Recuerda Esto',
    subscription_motivational_message: 'Recuerda, aprender inglés es un proceso. No ocurre de la noche a la mañana. No busques fluidez inmediata, sino busca mejorar cada día, aunque eso signifique solo aprender una nueva palabra. Un ladrillo no es mucho, pero si añades un ladrillo nuevo cada día, así es como construyes una casa.',
    subscription_motivational_continue: 'Seguir Aprendiendo',
    subscription_motivational_cancel: 'Cancelar De Todas Formas',
    subscription_error_cancel: 'Error al cancelar la suscripción',
    subscription_cancelled_pending: 'Suscripción Cancelada',
    subscription_cancelled_message: 'Tu suscripción ha sido cancelada pero aún tienes acceso hasta:',
    subscription_cancelled_on: 'Cancelada el',
    subscription_active: 'Suscripción Activa',
    subscription_level: 'Nivel',
    subscription_access_until: 'Acceso Hasta',
    subscription_expires: 'Expira',
    subscription_processor: 'Método de Pago',
    subscription_resubscribe: 'Suscribirse de Nuevo',
    subscription_extend: 'Extender / Actualizar Acceso',
    subscription_upgrade: 'Actualizar / Cambiar Plan',
    subscription_cancelling: 'Cancelando...',
    subscription_cancel: 'Cancelar Suscripción',
    subscription_manual_payment_info: 'Tu acceso expirará automáticamente el %s. Para extender tu acceso, simplemente realiza otro pago usando el botón de arriba.',
    subscription_manual_payment_label: 'Pago Manual:',
    subscription_inactive: 'Sin Suscripción Activa',
    subscription_inactive_message: 'No tienes una suscripción activa. Suscríbete para obtener acceso a todo el contenido de aprendizaje.',
    subscription_loading: 'Cargando...',
    subscription_subscribe: 'Obtener Suscripción (Stripe/PayPal)',
    subscription_mercadopago_manual: 'Pago Manual MercadoPago',
    subscription_help_text: '¿Necesitas ayuda con tu suscripción? Contáctanos en ',
    payment_divider_or: 'o',
    
    // Estado de pago manual
    pending_payment_review: 'Pago en Revisión',
    payment_under_review: 'Pago en Revisión',
    manual_payment_review_message: 'Tu comprobante de pago manual ha sido recibido y está siendo revisado por nuestro equipo.',
    amount: 'Monto',
    submitted: 'Enviado',
    pending_review: 'Pendiente de Revisión',
    manual_payment_timeline: 'Los pagos manuales son típicamente revisados dentro de 24 horas en días laborales.',
    
    // Página de pago manual enviado
    manual_payment_submitted_title: 'Comprobante de Pago Enviado',
    manual_payment_received: '¡Comprobante de Pago Recibido!',
    manual_payment_under_review: 'Tu pago está en revisión',
    manual_payment_review_explanation: 'Nuestro equipo verificará tu pago y activará tu suscripción dentro de 24 horas en días laborales.',
    payment_submitted_via: 'Pago enviado vía',
    manual_payment_timeline_detailed: 'Tiempo típico de revisión: 4-24 horas en días laborales',
    manual_payment_registration_message: 'Crea tu cuenta ahora para recibir notificaciones sobre el estado de tu pago y acceder a tu suscripción una vez aprobada.',
    manual_payment_benefit_1: 'Recibe notificaciones cuando tu pago sea aprobado',
    manual_payment_benefit_2: 'Sigue tu progreso de aprendizaje',
    manual_payment_benefit_3: 'Gestiona la configuración de tu suscripción',
    manual_payment_note_1: 'Recibirás confirmación por email una vez que tu pago sea aprobado',
    manual_payment_note_2: 'Tu suscripción se activará automáticamente después de la aprobación',
    manual_payment_note_3: 'Usa el mismo email que proporcionaste para el pago al registrarte',
    
    // Traducciones de pago de invitado
    enter_email_to_continue: 'Ingresa tu email para continuar',
    your_email_address: 'Tu dirección de email',
    please_enter_valid_email: 'Por favor ingresa un email válido',
    subscription_creation_error: 'Error al crear la suscripción',
    already_have_account: '¿Ya tienes una cuenta?',
    credit_card: 'Tarjeta de Crédito/Débito',
    choose_payment_method: 'Elegir Método de Pago',
    pay_with_credit_debit_card: 'Pagar con Tarjeta de Crédito/Débito',
    manual_payment_label: '(Pago Manual)',
    enter_email_to_continue_checkout: 'Por favor ingresa tu dirección de email arriba para continuar con el pago.',
    back: '← Volver',
    complete_your_payment: 'Completa tu Pago',
    
    // Mensajes de popup de validación
    requirements_not_met: 'Requisitos No Cumplidos',
    please_complete_following: 'Por favor completa lo siguiente para continuar:',
    enter_email_required: 'Ingresa tu dirección de email arriba',
    select_6_month_yearly: 'Selecciona un plan de 6+ meses',
    manual_payments_duration_required: 'Los pagos manuales solo están disponibles para planes de 6+ meses',
    paypal_not_available_usd: 'El pago manual de PayPal no está disponible para moneda USD',
    continue_button: 'Continuar',
    got_it: 'Entendido',
    dismiss: 'Cerrar',
    
    // Checkout de email mejorado
    email_checkout_options: 'Vincular tu correo electrónico',
    email_sync_explanation: 'Cómo funciona:',
    email_sync_details: 'Usa el mismo email para pagar ahora y registrarte después. Tu suscripción se sincronizará automáticamente cuando crees tu cuenta.',
    prefer_register_first: '¿Prefieres registrarte primero?',
    already_have_account_short: '¿Tienes cuenta?',
    accept_terms_prefix: 'Acepto los',
    terms_and_conditions: 'Términos y Condiciones',
    accept_terms_suffix: 'y estoy de acuerdo con los términos de suscripción.',
    subscription_terms_notice: 'Al suscribirte, aceptas los {TERMS_LINK}',
    subscription_terms_prefix: 'Al suscribirte, aceptas los',
    
    // Success/Cancel pages
    processing_payment: 'Procesando Pago',
    please_wait_processing: 'Por favor espera mientras procesamos tu pago...',
    payment_successful: '¡Pago Exitoso!',
    subscription_activated: '¡Tu Suscripción Ha Sido Activada!',
    guest_checkout_success_message: 'Tu pago se ha procesado exitosamente. Ahora puedes completar tu registro para acceder a todo el contenido.',
    complete_registration: 'Completar Registro',
    guest_registration_explanation: 'Crea tu cuenta para acceder a todas las funciones:',
    access_all_content: 'Acceso a todo el contenido',
    track_progress: 'Seguimiento de progreso',
    manage_subscription: 'Gestionar suscripción',
    create_account: 'Crear Cuenta',
    existing_user: 'Usuario Existente',
    existing_user_explanation: '¿Ya tienes una cuenta con este email?',
    login_to_access: 'Iniciar Sesión para Acceder',
    important_note: 'Nota Importante',
    email_sync_reminder: 'Usa el mismo email con el que pagaste para que tu suscripción se sincronice automáticamente.',
    questions_contact_support: '¿Tienes preguntas? Contáctanos en soporte@profezac.com',
    back_to_home: 'Volver al Inicio',
    subscription_success_message: 'Tu suscripción está ahora activa y puedes acceder a todo el contenido.',
    go_to_dashboard: 'Ir al Panel Principal',
    session_id: 'ID de Sesión',
    payment_cancelled: 'Pago Cancelado',
    subscription_not_activated: 'Tu Suscripción No Ha Sido Activada',
    guest_checkout_cancelled_message: 'Has cancelado el proceso de pago. No se aplicaron cargos a tu método de pago.',
    no_charges_applied: 'No se aplicaron cargos a tu cuenta.',
    
    // Página de Suscripción
    loading_pricing: 'Cargando Precios...',
    subscription_required: 'Suscripción Requerida',
    subscription_required_message: 'Por favor inicia sesión para ver las opciones de suscripción.',
    log_in: 'Iniciar Sesión',
    choose_learning_plan: 'Elije tu plan de accesso',
    all_plans_include: 'Todos los planes incluyen',
    unlock_english_potential: 'Desbloquea tu potencial en inglés con nuestros cursos completos en video',
    prices_shown_in: 'Precios mostrados en %s para %s',
    change_currency: 'Cambiar Moneda:',
    loading_currencies: 'Cargando monedas...',
    active_subscription: 'Suscripción Activa',
    current_access_level: 'Actualmente tienes acceso a contenido de nivel %s',
    expires: 'Expira: %s',
    weekly: 'Semanal',
    monthly: '1 mes',
    yearly: '1 año',
    save_percentage: '¡Ahorra %s!',
    best_value: 'MEJOR VALOR',
    week: 'semana',
    month: 'mes',
    year: 'año',
    more_features: '+%s características más',
    selected_plan: 'Plan Seleccionado',
    processing: 'Procesando...',
    pay_with_card: 'Pagar con Tarjeta de Crédito/Débito',
    mercadopago: 'MercadoPago',
    paypal: 'PayPal',
    secure_payment_cancel: 'Pago seguro • Cancela en cualquier momento',
    manual_transfer_note: 'Transferencia Manual: Sube captura de pantalla del pago para aprobación rápida',
    mercadopago_yearly_only: 'Los pagos de MercadoPago solo están disponibles para planes anuales',
    bancolombia_yearly_only: 'Los pagos de Bancolombia solo están disponibles para planes anuales',
    manual_payments_yearly_only: 'Los pagos manuales solo están disponibles para planes anuales',
    mercadopago_sixmonth_yearly_only: 'Los pagos de MercadoPago solo están disponibles para planes de 6 meses y anuales',
    bancolombia_sixmonth_yearly_only: 'Los pagos de Bancolombia solo están disponibles para planes de 6 meses y anuales',
    manual_payments_sixmonth_yearly_only: 'Los pagos manuales solo están disponibles para planes de 6 meses y anuales',
    six_month_plans_only: 'Solo disponible para planes de 6 meses o más',
    manual_payment_button: 'Pago Manual',
    
    // Pago Manual PayPal
    paypal_manual_payment: 'Pago Manual vía PayPal',
    paypal_payment_instructions: 'Instrucciones de Pago',
    paypal_step_1: 'Abre tu app de PayPal o visita paypal.com',
    paypal_step_2: 'Envía dinero a: asistente.zachmorris@gmail.com',
    paypal_step_3: 'Incluye los detalles de tu plan en la nota',
    paypal_step_4: 'Toma una captura de pantalla de la confirmación del pago',
    paypal_step_5: 'Sube la captura de pantalla abajo',
    paypal_send_to: 'Enviar pago a:',
    paypal_include_note: 'Incluye los detalles del plan en la nota:',
    paypal_clear_screenshot: 'Toma una captura de pantalla clara de la confirmación del pago',
    paypal_review_time: 'Tu acceso será aprobado en menos de 24 horas',
    paypal_confirmation_screenshot: 'Captura de Pantalla de Confirmación de Pago',
    paypal_select_screenshot: 'Seleccionar captura',
    paypal_change_file: 'Cambiar archivo',
    paypal_payment_summary: 'Resumen del Pago:',
    paypal_payment_to: 'Pago a:',
    paypal_currency: 'Moneda:',
    paypal_submit_payment: 'Enviar Pago',
    paypal_submitting: 'Enviando...',
    paypal_cancel: 'Cancelar',
    paypal_payment_submitted: '¡Pago Enviado!',
    paypal_submit_success: '¡Pago enviado exitosamente! Revisaremos tu pago dentro de 24 horas.',
    paypal_close: 'Cerrar',
    paypal_upload_required: 'Por favor sube una captura de pantalla de la confirmación de tu pago PayPal',
    paypal_file_too_large: 'El archivo es demasiado grande. Máximo 10MB.',
    paypal_invalid_file: 'Solo se permiten archivos de imagen (JPEG, PNG, GIF, WebP)',
    paypal_submit_error: 'Error enviando el pago',
    why_choose_profezac: '¿Por Qué Elegir Profezac?',
    interactive_videos: 'Videos Interactivos',
    interactive_videos_desc: 'Aprende con contenido de video profesional y atractivo',
    smart_quizzes: 'Cuestionarios Inteligentes',
    smart_quizzes_desc: 'Pon a prueba tu conocimiento con cuestionarios impulsados por IA',
    transcriptions: 'Transcripciones',
    transcriptions_desc: 'Lee junto con transcripciones con marcas de tiempo',
    progress_tracking: 'Seguimiento de Progreso',
    progress_tracking_desc: 'Monitorea tu viaje de aprendizaje',
    back_to_dashboard: 'Volver al Panel Principal',
    
    // Planes de Suscripción
    basic_english: 'Inglés Básico - Desde Cero',
    basic_english_desc: 'Sin conocimientos previos necesarios. Aprende inglés desde el primer día',
    advanced_english: 'Inglés Avanzado',
    advanced_english_desc: 'Para estudiantes intermedios listos para avanzar sus habilidades',
    native_level: 'Nivel Nativo',
    native_level_desc: 'Domina el inglés de nivel nativo con contenido avanzado',
    complete_access: 'Curso Completo',
    complete_access_desc: 'Acceso completo a todos los niveles - ¡El Mejor Valor!',
    
    // Características de Planes
    access_basic_videos: 'Acceso al nivel Básico',
    basic_selling_point: 'En este nivel, Profe Zac explica el inglés palabra por palabra, usando contexto audiovisual para que TODOS puedan entenderlo - a pesar de nunca entender inglés antes',
    interactive_quizzes_timestamps: 'Quizzes que garantizan comprensión',
    transcriptions_after_quiz: 'Subtítulos que acompañan todos los videos',
    progress_tracking_feature: 'Seguimiento de progreso',
    learn_180_key_words: 'Las 180 palabras claves para entender la mayoría de inglés hablado',
    videos_make_english_easy: 'Videos que hacen el inglés fácil de entender',
    access_any_device: 'Acceso desde cualquier dispositivo',
    everything_in_basic: 'Todo lo de Básico',
    access_advanced_videos: 'Acceso al nivel Avanzado',
    advanced_selling_point: 'Diseñado para complementar el nivel básico - este nivel explica las mismas palabras del nivel básico, pero con menos contexto, exigiendo que el usuario entienda no solo con contexto sino con comprensión también',
    advanced_vocabulary_grammar: 'Vocabulario y gramática avanzados',
    complex_conversation_scenarios: 'Escenarios de conversación complejos',
    priority_support: 'Soporte prioritario',
    everything_in_advanced: 'Todo lo de Avanzado',
    access_native_videos: 'Acceso al nivel Nativo',
    native_selling_point: 'Las MISMAS palabras del Básico y Avanzado pero SIN contexto audiovisual - exposición desafiante que acelera tu proceso de aprendizaje dramáticamente',
    native_speaker_expressions: 'Expresiones de hablante nativo',
    cultural_context_idioms: 'Contexto cultural y modismos',
    advanced_pronunciation_training: 'Entrenamiento avanzado de pronunciación',
    access_all_levels: 'Acceso a TODOS los niveles (Básico + Avanzado + Nativo)',
    all_levels_selling_point: 'Acceso a los 3 niveles te permitirá la experiencia completa - desde entender inglés solo con contexto hasta dominarlo sin contexto. Recomendado para aquellos que quieren acelerar su aprendizaje',
    
    // Plan title suffixes
    advanced_title_suffix: 'Profundizar',
    native_title_suffix: 'Dominio',
    all_levels_title_suffix: 'Aprendizaje Completo',
    
    unlimited_video_access: 'Acceso ilimitado a videos',
    unlimited_quiz_attempts: 'Intentos ilimitados de cuestionarios',
    all_transcriptions_included: 'Todas las transcripciones incluidas',
    new_content_released: 'Nuevo contenido cuando se lance',
    
    // Etiquetas de Duración
    per_week: 'por semana',
    per_month: 'por mes',
    per_year: 'por año',
    
    // Etiquetas Adicionales de Página de Suscripción
    select_plan: 'Por favor selecciona un plan de suscripción',
    pricing_load_error: 'No se pueden cargar los precios. Por favor actualiza la página.',
    currency_change_error: 'No se puede cambiar la moneda. Por favor intenta de nuevo.',
    
    // Validación de Formulario de Registro
    passwords_do_not_match: 'Las contraseñas no coinciden',
    password_too_short: 'La contraseña debe tener al menos 6 caracteres',
    
    // Inspiración Diaria
    daily_inspiration_title: 'Mensaje del Día de Profe Zac',
    daily_inspiration_loading: 'Cargando inspiración de hoy...',
    daily_inspiration_error: 'No se pudo cargar el mensaje diario',
    daily_inspiration_try_again: 'Intentar de Nuevo',
    daily_inspiration_refresh: 'Actualizar',
    daily_inspiration_video_message: 'Mensaje diario en video',
    daily_inspiration_text_message: 'Texto de inspiración diaria',
    daily_inspiration_today_goal: 'Meta de Hoy',
    daily_inspiration_today_goal_text: '¡Practica una nueva frase o conversación en inglés hoy!',
    daily_inspiration_remember: 'Recuerda',
    daily_inspiration_remember_text: 'Cada paso adelante es progreso. ¡Tú puedes!',
    daily_inspiration_personalized: 'Recomendación personalizada',
    daily_inspiration_new_lesson: '¡Nueva lección para ti!',
    daily_inspiration_continue_progress: '¡Continúa tu progreso!',
    daily_inspiration_go_to_lesson: 'Ir a la lección completa',
    daily_inspiration_show_inspiration: 'Inspiración diaria',
    
    // Home page join section
    ready_to_start: '¿Listo para Empezar a Aprender?',
    join_now: 'ÚNETE AHORA',
    home_join_description: 'Comienza a aprender una palabra en inglés por día y haz que el inglés sea más fácil todos los días. Este es el único curso hecho por un hablante nativo - diseñado para enseñarte las palabras más comunes en inglés.',
    
    // Preview overlay translations
    preview_unlock_text: 'Desbloquea acceso a este video y más de 180 lecciones con cuestionarios interactivos',
    get_full_access: 'Obtener Acceso Completo',
    subscription_modal_text: '¡Únete a miles dominando el inglés con nuestro curso completo!',
    subscription_videos_count: '180+ videos',
    subscription_interactive_quizzes: 'Cuestionarios interactivos',
    subscription_all_levels: 'Todos los niveles',
    start_learning_today: 'Comienza a Aprender Hoy',
    ready_for_more: '¿Listo para más?',
    sample_lesson_text: 'Lección de Inglés de Muestra',
    shuffle_video: 'Siguiente Video',
    
    // Sincronización de Membresía
    sync_paypal: 'Sincronizar Membresía',
    sync_membership: 'Sincronizar Membresía',
    sync_paypal_membership: 'Vincular Tu Suscripción',
    link_paypal_subscription: 'Vincular Tu Suscripción',
    membership_sync_desc: 'Si tienes una suscripción activa con PayPal, MercadoPago, etc. que no aparece en tu cuenta, usa este formulario para vincularla. Nuestro equipo verificará tu pago y te otorgará acceso.',
    subscription_details_optional: 'Detalles de Suscripción (Opcional)',
    subscription_id_label: 'ID de Suscripción',
    paypal_subscription_id: 'ID de Suscripción PayPal',
    subscription_id_placeholder: 'I-XXXXXXXXX (si lo tienes)',
    subscription_id_help: 'Esto se puede encontrar en tu cuenta PayPal bajo "Pagos Automáticos"',
    payment_information: 'Información de Pago',
    amount_paid: 'Cantidad Pagada',
    currency: 'Moneda',
    duration: 'Duración',
    course_access_level: 'Nivel de Acceso al Curso',
    payment_proof_optional: 'Comprobante de Pago (Opcional)',
    upload_payment_proof: 'Sube una captura de pantalla de tu pago o recibo (PNG, JPG, PDF - máx 5MB)',
    additional_notes: 'Notas Adicionales',
    additional_notes_placeholder: 'Cualquier información adicional sobre tu suscripción...',
    review_process: 'Proceso de Revisión:',
    review_process_desc: 'Nuestro equipo revisará tu solicitud en 1-2 días hábiles. Recibirás una notificación por email una vez aprobada.',
    usd_dollar: 'USD - Dólar Estadounidense',
    eur_euro: 'EUR - Euro',
    ars_peso: 'ARS - Peso Argentino',
    cop_peso: 'COP - Peso Colombiano',
    mxn_peso: 'MXN - Peso Mexicano',
    brl_real: 'BRL - Real Brasileño',
    gbp_pound: 'GBP - Libra Esterlina',
    cad_dollar: 'CAD - Dólar Canadiense',
    submit_request: 'Enviar Solicitud',
    submitting: 'Enviando...',
    cancel: 'Cancelar',
    your_previous_requests: 'Tus Solicitudes Anteriores',
    amount: 'Cantidad',
    level: 'Nivel',
    status: 'Estado',
    submitted: 'Enviado',
    pending_review: 'Pendiente de Revisión',
    approved: 'Aprobado',
    denied: 'Denegado',
    under_review: 'En Revisión',
    request_submitted_success: '✅ ¡Solicitud de sincronización enviada exitosamente! Nuestro equipo la revisará en 1-2 días hábiles.',
    monthly: '1 mes',
    yearly: '1 año',
    semiannual: '6 Meses',
    lifetime: 'De por Vida',
    basic_level: 'Nivel Básico',
    advanced_level: 'Nivel Avanzado',
    native_level: 'Nivel Nativo',
    all_levels: 'Todos los Niveles',
    
    // Términos y Condiciones
    terms_title: 'Términos de Servicio',
    terms_intro: 'Por favor lee estos términos cuidadosamente antes de usar nuestro servicio.',
    terms_section_1_title: '1. Acceso Inmediato al Curso',
    terms_section_1_p1: 'Una vez que hagas tu compra, tendrás acceso inmediato a través del portal.',
    terms_section_1_p2: 'Todo el contenido del curso está disponible a través de la plataforma del portal.',
    terms_section_2_title: '2. Planes de Suscripción y Cancelación',
    terms_section_2_p1: 'Ofrecemos planes de una semana, un mes y un año. Una vez que hayas adquirido el plan, se otorga acceso durante el período que se compró.',
    terms_section_2_p2: 'Tu membresía estará activa desde el momento en que compraste - es decir, desde el momento en que tienes acceso al curso, tu período de compra está activo.',
    terms_section_2_p3: 'Para cancelar tu suscripción, simplemente ve al panel de control y dentro del dashboard del usuario encontrarás la sección donde administras tu suscripción.',
    terms_section_2_p4: 'Aunque hayas cancelado tu membresía, aún tendrás acceso hasta el final del período por el cual se compró tu plan.',
    terms_section_2_p5: 'Es tu responsabilidad cancelar tu suscripción antes de la fecha de renovación para evitar ser cobrado automáticamente por el siguiente ciclo de facturación.',
    terms_section_3_title: '3. Política de No Reembolso y No Devolución',
    terms_section_3_p1: 'Debido al alto valor y la accesibilidad de nuestro servicio, no ofrecemos reembolsos ni devoluciones.',
    terms_section_3_p2: 'Recomendamos adquirir el plan semanal o mensual inicialmente para evaluar el curso antes de optar por planes más caros.',
    terms_section_3_p3: 'Al comprar el curso, confirmas que entiendes que todas las ventas son finales y no se emitirán reembolsos.',
    terms_section_3_p4: 'Disputar un cargo legítimo podría resultar en la suspensión o terminación de tu cuenta.',
    terms_section_4_title: '4. Métodos de Pago',
    terms_section_4_p1: 'Los métodos de pago aceptados incluyen tarjetas de crédito/débito, PayPal, Bitcoin, Mercado Pago y Bancolombia.',
    terms_section_4_p2: 'También se ofrecen métodos de pago locales en algunos países.',
    terms_section_5_title: '5. Acceso al Contenido y Propiedad',
    terms_section_5_p1: 'Cuando compras un plan de suscripción, estás pagando por el acceso a nuestro contenido educativo, incluyendo videos, cuestionarios y transcripciones. Esta suscripción te otorga el derecho de acceder y usar los materiales del curso durante tu período activo de suscripción.',
    terms_section_5_p2: 'Ten en cuenta que el contenido del curso, incluyendo todos los videos, cuestionarios, transcripciones y materiales relacionados, siguen siendo propiedad intelectual de English with Profezac. Tu suscripción no te transfiere la propiedad de ningún contenido, y no puedes redistribuir, copiar o compartir los materiales del curso con otros.',
    terms_section_6_title: '6. Descuentos y Promociones',
    terms_section_6_content: 'Marcador de posición para el contenido de la sección 6 - será actualizado.',
    terms_section_7_title: '7. Filosofía de Aprendizaje',
    terms_section_7_content: 'Marcador de posición para el contenido de la sección 7 - será actualizado.',
    terms_section_8_title: '8. Privacidad y Seguridad de Datos',
    terms_section_8_content: 'Marcador de posición para el contenido de la sección 8 - será actualizado.',
    terms_section_9_title: '9. Aceptación de los Términos',
    terms_section_9_p1: 'Al adquirir y acceder al curso, reconoces y aceptas estos Términos de Servicio.',
    terms_section_9_p2: 'Estos términos están sujetos a cambios, y las actualizaciones se publicarán en nuestro sitio web oficial.',
    
    // BasicLevelLanding translations
    basic_english: 'Inglés Básico',
    learn_180_key_words: 'Aprende las 180 palabras clave para entender la mayoría del inglés hablado',
    videos_make_english_easy: 'Videos que hacen que el inglés sea fácil',
    basic_english_desc: 'Inglés desde cero hasta entender conversaciones básicas',
    monthly: 'mes',
    free_no_commitment: 'Sin compromiso',
    start_learning_today: 'Comienza a aprender hoy',
    how_basic_level_works: 'Cómo Funciona el Nivel Básico',
    basic_level_description: 'Comienza tu viaje en inglés con vocabulario esencial y pronunciación',
    watch_basic_demo: 'Ver y Aprender',
    basic_description: 'Mira como Profe Zac explica el inglés usando contextos del mundo real que entiendes',
    practice_quiz: 'Practicar y Quiz',
    test_understanding_quizzes: 'Pon a prueba tu comprensión con cuestionarios interactivos después de cada lección',
    simple_vocabulary: 'Construye Vocabulario',
    home_get_started_free: 'Comenzar Ahora',
    profe_confidence_message: 'Profe Zac no solo enseña palabras - te muestra cómo funciona el inglés en situaciones reales',
    basketball_basics_desc: 'Contexto real, no ejemplos aburridos de libros de texto',
    subtitles_connect_sounds: 'Subtítulos para ayudarte a conectar sonidos con palabras',
    try_real_basic_quiz: 'Prueba un Quiz Real de Nivel Básico',
    start_english_journey_today: 'Comienza Tu Viaje en Inglés Hoy',
    access_all_basic_videos: 'Acceso a todos los videos de Nivel Básico',
    interactive_quizzes_feedback: 'Cuestionarios interactivos con retroalimentación instantánea',
    progress_tracking_improvement: 'Seguimiento de progreso para ver tu mejora',
    subtitles_transcriptions_included: 'Subtítulos y transcripciones incluidos',
    start_course: 'Comenzar a Aprender Inglés Desde 0',
    learn_english_from_zero_with_profe_zac: 'Aprende Inglés Desde 0 con Profe Zac',
    about_to_start_conversations: 'Estás a punto de empezar a entender conversaciones en inglés',
    want_to_learn_english_from_zero: 'Quiero aprender inglés desde 0',
    motivational_text_base: 'Si hasta ahora no has logrado aprender inglés, no es porque no puedas, es porque nadie te ha enseñado de la manera correcta. Y aquí es donde entra Inglés con Profe Zac. Este curso fue creado con un solo propósito: hacer que aprender inglés sea fácil, accesible y efectivo para',
    latino_community_default: 'la comunidad latina',
    cancel_anytime: 'Cancelar en cualquier momento',
    start_immediately: 'Comenzar inmediatamente',
    ready_learn_english_zero: '¿Listo para Aprender Inglés desde Cero?',
    join_thousands_students: 'Únete a miles de estudiantes que comenzaron sin inglés y ahora entienden conversaciones',
    get_started_today: 'Comenzar Hoy',
    
    // Quiz instructions
    quiz_instruction_distractor: 'Encuentra las palabras correctas y ordénalas correctamente según lo que escuchaste en el video:'
  },
  
  [LANGUAGES.PT]: {
    // Navegação
    nav_home: 'Início',
    nav_dashboard: 'Painel Principal',
    nav_login: 'Entrar',
    nav_register: 'Registrar',
    nav_logout: 'Sair',
    nav_signup: 'Inscrever-se',
    nav_account: 'Conta',
    nav_levels: 'Níveis',
    nav_access_requests: 'Solicitações de Acesso',
    courses: 'Cursos',
    site_title: 'Inglês com Profezac',
    
    // Níveis
    level_basic: 'Básico',
    level_advanced: 'Avançado',
    level_native: 'Nativo',
    level_all: 'Todos os Níveis',
    
    // Página inicial
    home_title: 'Domine o Inglês no Seu Próprio Ritmo',
    home_subtitle: 'Junte-se ao nosso curso completo de inglês com lições diárias em vídeo nos níveis Básico, Avançado e Nativo. Melhore suas habilidades com questionários interativos e acompanhe seu progresso.',
    home_get_started_free: 'Começar Grátis',
    home_login: 'Entrar',
    home_try_demos: 'Ou experimente nossas demonstrações gratuitas de vídeo:',
    home_demo_title: 'Experimente Nossas Demonstrações Gratuitas de Vídeo e Quiz',
    home_demo_subtitle: 'Experimente cada nível com nossas amostras de lições de basquete',
    not_ready_sign_up: '(ainda não está pronto para se inscrever?)',
    
    // Cartões de nível
    basic_level: 'Nível Básico',
    advanced_level: 'Nível Avançado',
    native_level: 'Nível Nativo',
    basketball_basics: 'Fundamentos de Basquete',
    basketball_techniques: 'Técnicas de Basquete',
    basketball_stories: 'Histórias de Basquete',
    basketball_basics_desc: 'Aprenda vocabulário simples: "Isto é um basquete. Eu dribo o basquete."',
    basketball_techniques_desc: '"Isto é uma bandeja. Quando você erra e pega a bola, isso é um rebote."',
    basketball_stories_desc: '"Fui cortado da equipe do ensino médio. O basquete favorece a altura..."',
    
    // Listas de características
    simple_vocabulary: 'Vocabulário simples',
    clear_pronunciation: 'Pronúncia clara',
    basic_sentence_patterns: 'Padrões básicos de sentenças',
    technical_vocabulary: 'Vocabulário técnico',
    detailed_explanations: 'Explicações detalhadas',
    complex_sentences: 'Sentenças complexas',
    natural_conversation: 'Conversa natural',
    personal_stories: 'Histórias pessoais',
    cultural_context: 'Contexto cultural',
    
    // Botões de demonstração
    watch_basic_demo: 'Assistir Demonstração Básica',
    watch_advanced_demo: 'Assistir Demonstração Avançada',
    watch_native_demo: 'Assistir Demonstração Nativa',
    
    // Descrições de níveis
    basic_description: 'Perfeito para iniciantes. Aprenda vocabulário e gramática essenciais para iniciar sua jornada em inglês.',
    advanced_description: 'Para estudantes intermediários. Melhore sua fluência com estruturas e vocabulário mais complexos.',
    native_description: 'Domine as nuances do inglês com expressões idiomáticas, referências culturais e habilidades de comunicação avançadas.',
    learn_more: 'Saiba Mais',

    // Painel Principal
    dashboard_title: 'Painel Principal',
    welcome: 'Bem-vindo',
    dashboard_intro: 'Acesse seu conteúdo de aprendizagem de inglês aqui.',
    subscription_status: 'Status da Assinatura',
    subscription_active_message: 'Sua assinatura está ativa!',
    subscription_inactive_message: 'Você não tem uma assinatura ativa.',
    subscription_level: 'Nível',
    subscription_level_basic: 'Básico',
    subscription_level_advanced: 'Avançado',
    subscription_level_native: 'Nativo',
    subscription_level_all: 'Todos os Níveis',
    subscription_expires: 'Expira',
    payment_method: 'Método de Pagamento',
    manage_subscription: 'Gerenciar Assinatura',
    get_subscription: 'Obter Assinatura',
    start_secure_checkout: 'Iniciar Pagamento Seguro',
    renew_subscription: 'Renovar Assinatura',
    reactivate_subscription: 'Reativar Assinatura',
    subscription_cancelled: 'Assinatura Cancelada',
    subscription_cancelled_message: 'Você ainda tem acesso até',
    cancelled_on: 'Cancelada em',
    access_until: 'Acesso até',
    subscription_expired_message: 'Sua assinatura expirou em',
    previous_payment_method: 'Método de Pagamento Anterior',
    learning_levels: 'Níveis de Aprendizagem',
    learn_about_levels: 'Saiba Sobre os Níveis',
    level_basic_desc: 'Inglês fundamental para iniciantes.',
    level_advanced_desc: 'Prática de inglês intermediário a avançado.',
    level_native_desc: 'Conteúdo autêntico de falante nativo.',
    explore_level: 'Explorar Nível',
    free_preview: 'Visualização Gratuita',
    unlock_all: 'Desbloquear Tudo',
    
    // Proposta de valor gratuito
    profe_confidence_message: 'O Profe Zac está tão confiante de que você vai amar aprender inglês com seus vídeos que está oferecendo acesso gratuito (um valor de $8)!',
    free_access_includes: 'Seu acesso GRATUITO inclui:',
    free_video_lessons: '3 Lições em Vídeo + Questionários Interativos',
    free_all_levels: '(Níveis Básico, Avançado e Nativo)',
    
    // Dashboard course access
    course_access: 'Acesso ao Curso',
    complete_english_course: 'Curso Completo de Inglês',
    course_access_description: 'Acesse todas as 180 palavras essenciais do inglês organizadas por nível.\nDomine o vocabulário com as aulas em vídeo abrangentes do Professor Zac.',
    total_words: 'Total de Palavras',
    skill_levels: 'Níveis de Habilidade',
    practice_quizzes: 'Questionários de Prática',
    start_learning_now: 'Comece a Aprender Agora',
    full_access_all_levels: 'Você tem acesso completo a todos os níveis!',
    access_basic_advanced_native: 'Acesso ao Básico, Avançado e Nativo',
    access_basic_advanced: 'Acesso ao Básico e Avançado',
    access_basic_level: 'Acesso ao nível Básico',
    signup_start_journey: 'Cadastre-se para iniciar sua jornada em inglês',
    
    // Video card descriptions - fallback text
    learn_word_with_professor: 'Aprenda esta palavra com o Professor Zac',
    
    // Dynamic description translations
    learn_the_activity: 'Aprenda a atividade',
    learn_conversation_about: 'Aprenda conversa sobre',
    learn_the_verb: 'Aprenda o verbo',
    learn_the_noun: 'Aprenda o substantivo',
    learn_the_adjective: 'Aprenda o adjetivo',
    learn_the_adverb: 'Aprenda o advérbio',
    learn_the_preposition: 'Aprenda a preposição',
    learn_the_word: 'Aprenda o/a',
    in_level_english: 'em inglês nível %s',
    
    // Archive page text
    all_words_complete_archive: 'Todas as 180 Palavras - Arquivo Completo',
    browse_all_english_words: 'Navegue por todas as palavras em inglês em todos os níveis',
    back_to_dashboard: 'Voltar ao Painel',
    search_words_placeholder: 'Pesquisar palavras...',
    your_access_level: 'Seu Nível de Acesso',
    all_progress: 'Todo o Progresso',
    not_started: 'Não Iniciado',
    in_progress: 'Em Progresso',
    completed: 'Concluído',
    words_found: 'Palavras Encontradas',
    no_words_found: 'Nenhuma palavra encontrada',
    try_adjusting_filters: 'Tente ajustar sua pesquisa ou critérios de filtro',
    watch: 'Assistir',
    upgrade: 'Atualizar',
    upgrade_for_full_access: 'Atualizar para Acesso Completo',
    filter_by_progress: 'Filtrar por Progresso:',
    free_daily_inspiration: 'Vídeos de Inspiração Diários',
    free_progress_tracking: 'Painel de Acompanhamento de Progresso',
    free_no_payment: 'Nenhum pagamento necessário - Comece imediatamente!',
    free_no_commitment: 'Sem compromisso algum',
    free_value_amount: 'Valor $8 - Seu GRATUITAMENTE',
    
    // Conteúdo freemium
    freemium_header: 'Prévia Gratuita',
    freemium_subtitle: 'vídeos de amostra',
    freemium_viewing_message: 'Você está visualizando',
    freemium_from_collection: 'da nossa coleção de nível',
    freemium_level_collection: '',
    freemium_upgrade_message: 'Atualize para acessar todos os',
    freemium_videos_and_quizzes: 'vídeos e questionários ilimitados!',
    freemium_upgrade_now: 'Atualizar Agora',
    freemium_quiz_attempts: 'Tentativas de Questionário',
    freemium_quiz_limit_reached: 'Limite de Questionário Atingido',
    freemium_available_videos: 'Vídeos Disponíveis',
    freemium_want_access: 'Quer acesso a todos os',
    freemium_unlock_videos: 'Desbloquear Todos os Vídeos',
    freemium_select_video: 'Selecione um vídeo para começar a aprender',
    freemium_choose_from: 'Escolha entre os',
    freemium_available_videos_text: 'vídeos freemium disponíveis',
    freemium_interactive_quiz: 'Questionário Interativo',
    freemium_attempts_used: 'tentativas usadas',
    freemium_quiz_limit_message: 'Você usou todas as suas tentativas gratuitas de questionário.',
    freemium_upgrade_unlimited: 'Atualize para Questionários Ilimitados',
    loading_freemium_content: 'Carregando conteúdo freemium...',
    loading_quiz: 'Carregando questionário...',
    quiz_error: 'Erro de Questionário',
    your_subscription: 'Sua assinatura',
    today_lessons: 'Lições de Hoje',
    today_video: 'Lição em Vídeo de Hoje',
    watch_video: 'Assistir Vídeo',
    video_archives: 'Arquivo de Vídeos',
    access_previous: 'Acesse Lições Anteriores',
    browse_archive: 'Explore nosso arquivo de lições em vídeo por nível.',
    
    // Gestão de Nível de Acesso
    access_level_management: 'Gestão de Nível de Acesso',
    request_access_change: 'Solicitar Mudança de Acesso',
    cancel_request: 'Cancelar Solicitação',
    view_my_requests: 'Ver Minhas Solicitações',
    hide_my_requests: 'Ocultar',
    your_access_change_requests: 'Suas Solicitações de Mudança de Acesso',
    no_access_requests: 'Nenhuma solicitação de mudança de acesso encontrada.',
    request_submitted_success: 'Sua solicitação de mudança de acesso foi enviada com sucesso! Um administrador irá revisá-la em breve.',
    request_submit_failed: 'Falha ao enviar solicitação',
    network_error: 'Erro de rede. Tente novamente.',
    request_access_change_form: 'Solicitar Mudança de Acesso',
    requested_access_level: 'Nível de Acesso Solicitado',
    select_level: 'Selecione um nível...',
    reason_for_request: 'Motivo da Solicitação',
    reason_placeholder: 'Por favor, explique por que você precisa desta mudança de nível de acesso. Inclua detalhes sobre sua assinatura ou quaisquer problemas que esteja enfrentando...',
    characters: 'caracteres',
    
    // New adaptive translations
    request_access: 'Solicitar Acesso',
    change_access_level: 'Alterar Nível de Acesso',
    no_active_access_message: 'Você não tem acesso ativo no momento. Solicite acesso para começar a aprender!',
    request_access_form: 'Solicitar Acesso',
    request_access_help_text: 'Se você acredita que deveria ter acesso ou está enfrentando problemas com sua assinatura, envie uma solicitação abaixo.',
    request_access_reason_placeholder: 'Por favor, explique por que você precisa de acesso. Por exemplo: "Paguei mas não vejo minha assinatura" ou "Minha assinatura expirou mas quero continuar aprendendo"...',
    your_subscription_info: 'Informações da Sua Assinatura',
    current_level: 'Nível Atual',
    other_levels: 'Outros Níveis',
    view_level: 'Ver Nível %s',
    upgrade_required_level: 'Atualização Necessária para %s',
    access_denied_level: 'Acesso negado para o nível %s. Por favor, atualize sua assinatura.',
    access_denied_title: 'Acesso Negado',
    access_denied_message: 'Você não tem acesso ao nível %s.',
    upgrade_to_access_message: 'Atualize sua assinatura para desbloquear todos os níveis e continuar aprendendo.',
    upgrade_membership: 'Atualizar Assinatura',
    back: 'Voltar',
    cancel: 'Cancelar',
    submit_request: 'Enviar Solicitação',
    submitting: 'Enviando...',
    requested: 'Solicitado',
    reason: 'Motivo',
    admin_notes: 'Notas do Administrador',
    basic_level_desc_short: 'Conteúdo fundamental de aprendizado de inglês',
    advanced_level_desc_short: 'Conteúdo de inglês mais desafiador',
    native_level_desc_short: 'Conteúdo de nível de falante nativo',
    all_levels_desc: 'Acesso a todos os níveis de conteúdo',
    
    // Autenticação
    email_address: 'Email',
    password: 'Senha',
    confirm_password: 'Confirmar Senha',
    first_name: 'Nome',
    last_name: 'Sobrenome',
    already_account: 'Já tem uma conta?',
    no_account: 'Não tem uma conta?',
    register_here: 'Registre-se aqui',
    login_here: 'Entre aqui',
    forgot_password: 'Esqueceu sua senha?',
    logging_in: 'Entrando...',
    registering: 'Registrando...',
    
    // Vídeos
    back_to_dashboard: 'Voltar ao Painel Principal',
    video_of_day: 'Lição de Inglês %s de Hoje',
    description: 'Descrição',
    published_on: 'Publicado em',
    view_archive: 'Ver Arquivo',
    browse_archive_level: 'Explorar Arquivo %s',
    no_video: 'Nenhum vídeo disponível',
    error_loading: 'Erro ao carregar o vídeo',
    try_again: 'Tentar Novamente',
    about_lesson: 'Sobre esta lição',
    learn_adjective: 'Aprenda o adjetivo "%s" em inglês nível básico',
    
    // Transcrição
    transcription_locked: 'Transcrição Bloqueada',
    complete_quiz_unlock: 'Complete o questionário para este vídeo para desbloquear a transcrição',
    
    // Questionário
    take_quiz: 'Fazer Questionário',
    quiz_results: 'Resultados do Questionário',
    test_understanding: 'Teste Sua Compreensão',
    submit_quiz: 'Enviar Questionário',
    next_question: 'Próxima Pergunta',
    previous_question: 'Pergunta Anterior',
    hide_quiz: 'Ocultar Questionário',
    generate_quiz: 'Gerar Questionário',
    loading_quiz: 'Carregando Questionário...',
    generating_quiz: 'Gerando Questionário...',
    watch_segment: 'Assistir Segmento',
    pause_segment: 'Pausar Segmento',
    press_again_if_cut_short: '💡 Pressione novamente se foi cortado',
    
    // Quiz instructions
    arrange_words_instruction: 'Organize essas palavras na ordem correta para combinar com o que você ouviu no vídeo:',
    select_correct_answers_instruction: 'Com base no que você viu no vídeo, selecione todas as respostas corretas:',
    quiz_instruction_distractor: 'Encontre as palavras corretas e organize-as na ordem correta para corresponder ao que você ouviu no vídeo:',
    
    your_score: 'Sua Pontuação',
    answered_questions: 'Você respondeu corretamente %s de %s perguntas.',
    question_review: 'Revisão de Perguntas',
    question: 'Pergunta',
    of: 'de',
    correct: 'Correto',
    incorrect: 'Incorreto',
    your_answer: 'Sua resposta',
    correct_answer: 'Resposta correta',
    explanation: 'Explicação',
    no_answer: '[Sem resposta]',
    error_during_submission: 'Erro durante o envio',
    back_to_video: 'Voltar ao Vídeo',
    quiz_not_found: 'Questionário Não Encontrado',
    quiz_not_found_message: 'O questionário solicitado não pôde ser encontrado ou carregado.',
    loading_question: 'Carregando pergunta...',
    go_to_question: 'Ir para a pergunta',
    error: 'Erro',
    submitting: 'Enviando...',
    
    // Estado da assinatura
    active: 'ativa',
    expired: 'vencida',
    cancelled: 'cancelada',
    pending: 'pendente',
    
    // Footer
    footer_rights: 'Todos os Direitos Reservados',
    footer_privacy: 'Política de Privacidade',
    footer_terms: 'Termos de Uso',
    footer_contact: 'Contate-nos',
    
    // VideoArchive
    filter_by_type: 'Filtrar por tipo',
    all_types: 'Todos os Tipos',
    showing_videos: 'Mostrando %s de %s vídeos',
    no_videos_found: 'Nenhum vídeo encontrado para este nível e tipo.',
    general: 'Geral',
    previous: 'Anterior',
    next: 'Próximo',
    
    // Página de Explicação de Níveis
    levels_explained_title: 'Domine o Inglês no Seu Próprio Ritmo',
    levels_explained_subtitle: 'Junte-se ao nosso curso completo de inglês com lições diárias em vídeo nos níveis Básico, Avançado e Nativo. Melhore suas habilidades com questionários interativos e acompanhe seu progresso.',
    basketball_example_intro: 'Exemplo: Aprendendo "Basketball"',
    basketball_example_desc: 'Veja como o mesmo tópico é ensinado em todos os três níveis. Cada nível se baseia no anterior, proporcionando a progressão perfeita para sua jornada de aprendizado de inglês.',
    what_youll_learn: 'O que Você Vai Aprender:',
    basketball_example: 'Exemplo de Basketball:',
    perfect_for: 'Perfeito para:',
    watch_sample_video: 'Assistir Vídeo de Exemplo',
    free_demo_signup: 'Demo gratuita - Inscreva-se para acessar o conteúdo completo',
    how_learning_works: 'Como Nosso Sistema de Aprendizado Funciona',
    watch_videos: 'Assistir Vídeos',
    watch_videos_desc: 'Assista lições diárias em vídeo adaptadas ao seu nível com explicações claras e exemplos do mundo real.',
    take_quizzes: 'Fazer Questionários',
    take_quizzes_desc: 'Complete questionários interativos que se adaptam ao seu nível e ajudam a reforçar o que você aprendeu.',
    track_progress: 'Acompanhar Progresso',
    track_progress_desc: 'Monitore sua melhoria e desbloqueie transcrições enquanto domina cada lição.',
    ready_to_start: 'Pronto para Começar a Aprender?',
    ready_to_start_desc: 'Junte-se a milhares de estudantes melhorando seu inglês com nosso método comprovado. Escolha seu nível e comece sua jornada hoje!',
    sign_up_free: 'Inscrever-se Grátis',
    login: 'Entrar',
    faq_title: 'Perguntas Frequentes',
    faq_which_level: 'Com qual nível devo começar?',
    faq_which_level_answer: 'Comece com Básico se você é novo no inglês, Avançado se você pode ter conversas simples, ou Nativo se você quer aperfeiçoar sua fluência com conteúdo autêntico.',
    faq_multiple_levels: 'Posso acessar múltiplos níveis?',
    faq_multiple_levels_answer: 'Sim! Nossos planos de assinatura permitem acesso ao seu nível escolhido e todos os níveis inferiores. O plano "Todos os Níveis" dá acesso completo a todo o conteúdo.',
    faq_how_quizzes: 'Como funcionam os questionários?',
    faq_how_quizzes_answer: 'Depois de assistir um vídeo, você fará um questionário interativo baseado no conteúdo. Passe no questionário para desbloquear a transcrição do vídeo e acompanhar seu progresso.',
    
    // Conteúdo específico de níveis
    basic_level_title: 'Nível Básico',
    basic_level_description: 'Perfeito para iniciantes aprendendo vocabulário fundamental de inglês e estruturas de frases simples.',
    basic_features_1: 'Introdução de vocabulário simples',
    basic_features_2: 'Pronúncia clara',
    basic_features_3: 'Padrões básicos de frases',
    basic_features_4: 'Repetição para aprendizado',
    basic_example_text: 'This is a BASKETBALL. A BASKETBALL. This is a BASKETBALL COURT. I DRIBBLE the BASKETBALL. I SHOOT the BASKETBALL.',
    basic_audience: 'Iniciantes com pouca ou nenhuma experiência em inglês',
    
    advanced_level_title: 'Nível Avançado',
    advanced_level_description: 'Para estudantes intermediários prontos para expandir vocabulário e entender conceitos mais complexos.',
    advanced_features_1: 'Vocabulário avançado',
    advanced_features_2: 'Explicações detalhadas',
    advanced_features_3: 'Estruturas de frases complexas',
    advanced_features_4: 'Aprendizado condicional e contextual',
    advanced_example_text: 'This is a layup. This is a rebound. When you shoot and you miss and you catch the ball, that\'s a rebound. A jump shot is when you jump and you shoot.',
    advanced_audience: 'Estudantes intermediários confortáveis com inglês básico',
    
    native_level_title: 'Nível Nativo',
    native_level_description: 'Conteúdo autêntico de falante nativo com conversa natural, expressões idiomáticas e contexto cultural.',
    native_features_1: 'Fluxo de conversa natural',
    native_features_2: 'Experiências pessoais e histórias',
    native_features_3: 'Contexto cultural e nuances',
    native_features_4: 'Gramática e expressões complexas',
    native_example_text: 'I really enjoy basketball because when you play basketball, you don\'t have to think about anything else. Basketball is a sport that favors height. I got cut from the high school team.',
    native_audience: 'Estudantes avançados buscando fluência de nível nativo',
    
    // Página de Demo do Curso
    course_demo_title: 'Demo de %s',
    course_demo_subtitle: 'Experimente nosso conteúdo de nível %s com esta lição real de basquete',
    basketball_level_title: 'Basquete - %s',
    sample_content: 'Conteúdo de Exemplo:',
    try_interactive_quiz: 'Experimente o Questionário Interativo',
    interactive_quiz_title: 'Questionário Interativo: %s',
    interactive_quiz_preview: 'Prévia do Questionário Interativo',
    quiz_preview_intro: 'Após assistir o vídeo, você fará um questionário com perguntas como:',
    sample_question: 'Pergunta de Exemplo:',
    complete_quiz_unlock_transcript: 'Complete o questionário para desbloquear a transcrição completa com marcações de tempo palavra por palavra!',
    what_youll_learn_level: 'O que Você Vai Aprender em %s',
    ready_to_start_learning: 'Pronto para Começar a Aprender?',
    course_demo_cta_text: 'Isto é apenas uma prévia! Inscreva-se para acessar centenas de vídeos em todos os níveis.',
    compare_all_levels: 'Comparar todos os níveis',
    loading_demo_content: 'Carregando conteúdo de demonstração...',
    error_loading_demo: 'Erro ao Carregar Demonstração',
    try_again: 'Tentar Novamente',
    level_not_found: 'Nível não encontrado',
    real_video_streaming: 'Streaming de vídeo real disponível na versão completa',
    demo_mode_notice: 'Modo demo: Conexão com banco de dados necessária para vídeo real',
    fully_functional_demo: 'Esta é uma demonstração totalmente funcional! Na versão completa, completar questionários desbloqueia transcrições com marcações de tempo palavra por palavra.',
    
    // Descrições de Níveis do Demo do Curso
    basic_video_description: 'Nesta lição de basquete, você aprenderá vocabulário básico como \'basketball\', \'court\', e \'goal\'. O professor fala devagar e claramente, repetindo cada palavra várias vezes para ajudá-lo a memorizar e pronunciar corretamente.',
    advanced_video_description: 'Esta lição avançada de basquete introduz termos técnicos como \'layup\', \'rebound\', e \'jump shot\'. Você aprenderá explicações e condições (quando X acontece, então Y) para entender a mecânica do basquete.',
    native_video_description: 'Experimente fala nativa autêntica sobre basquete. O professor compartilha histórias pessoais sobre ser cortado da equipe do ensino médio e discute como a altura afeta o desempenho no basquete - percepções culturais reais que você não encontrará em livros didáticos.',
    
    // Perguntas do Questionário - Nível Básico
    basic_quiz_question_1: 'Organize estas palavras na ordem correta:',
    basic_quiz_question_2: 'O que você faz com um basquete?',
    basic_quiz_answer_dribble: 'Driblar',
    basic_quiz_answer_eat: 'Comer',
    basic_quiz_answer_read: 'Ler',
    basic_quiz_answer_sleep: 'Dormir',
    
    // Perguntas do Questionário - Nível Avançado
    advanced_quiz_question_1: 'O que é um rebote no basquete?',
    advanced_quiz_answer_1_correct: 'Quando você pega a bola após um arremesso perdido',
    advanced_quiz_answer_1_wrong_1: 'Quando você marca pontos',
    advanced_quiz_answer_1_wrong_2: 'Quando você passa a bola',
    advanced_quiz_answer_1_wrong_3: 'Quando você inicia o jogo',
    advanced_quiz_question_2: 'Um jump shot é quando você [ESPAÇO 1] e você [ESPAÇO 2].',
    
    // Perguntas do Questionário - Nível Nativo
    native_quiz_question_1: 'O que significa "got cut from the team"?',
    native_quiz_answer_1_correct: 'Foi removido da equipe',
    native_quiz_answer_1_wrong_1: 'Se machucou jogando',
    native_quiz_answer_1_wrong_2: 'Ganhou o campeonato',
    native_quiz_answer_1_wrong_3: 'Tornou-se capitão',
    native_quiz_question_2: 'Por que o falante diz que o basquete "favorece a altura"?',
    native_quiz_answer_2_correct: 'Jogadores mais altos têm vantagens',
    native_quiz_answer_2_wrong_1: 'Quadras de basquete são altas',
    native_quiz_answer_2_wrong_2: 'A bola é pesada',
    native_quiz_answer_2_wrong_3: 'O jogo é difícil',
    
    // Página de Gerenciamento de Assinatura
    subscription_title: 'Gerenciamento de Assinatura',
    subscription_login_required: 'Login Necessário',
    subscription_login_message: 'Você precisa fazer login para gerenciar sua assinatura',
    subscription_error_redirect: 'Erro ao redirecionar para o portal de pagamentos',
    subscription_confirm_cancel: 'Tem certeza de que deseja cancelar sua assinatura?',
    subscription_motivational_title: 'Antes de Cancelar - Lembre-se Disso',
    subscription_motivational_message: 'Lembre-se, aprender inglês é um processo. Não acontece da noite para o dia. Não procure fluência imediata, mas sim procure melhorar todos os dias, mesmo que isso signifique apenas aprender uma nova palavra. Um tijolo não é muito, mas se você adicionar um tijolo novo todos os dias, é assim que se constrói uma casa.',
    subscription_motivational_continue: 'Continuar Aprendendo',
    subscription_motivational_cancel: 'Cancelar Mesmo Assim',
    subscription_error_cancel: 'Erro ao cancelar assinatura',
    subscription_cancelled_pending: 'Assinatura Cancelada',
    subscription_cancelled_message: 'Sua assinatura foi cancelada, mas você ainda tem acesso até:',
    subscription_cancelled_on: 'Cancelada em',
    subscription_active: 'Assinatura Ativa',
    subscription_level: 'Nível',
    subscription_access_until: 'Acesso Até',
    subscription_expires: 'Expira',
    subscription_processor: 'Método de Pagamento',
    subscription_resubscribe: 'Assinar Novamente',
    subscription_extend: 'Estender / Atualizar Acesso',
    subscription_upgrade: 'Atualizar / Mudar Plano',
    subscription_cancelling: 'Cancelando...',
    subscription_cancel: 'Cancelar Assinatura',
    subscription_manual_payment_info: 'Seu acesso expirará automaticamente em %s. Para estender seu acesso, simplesmente faça outro pagamento usando o botão acima.',
    subscription_manual_payment_label: 'Pagamento Manual:',
    subscription_inactive: 'Nenhuma Assinatura Ativa',
    subscription_inactive_message: 'Você não tem uma assinatura ativa. Assine para ter acesso a todo o conteúdo de aprendizado.',
    subscription_loading: 'Carregando...',
    subscription_subscribe: 'Obter Assinatura (Stripe/PayPal)',
    subscription_mercadopago_manual: 'Pagamento Manual MercadoPago',
    subscription_help_text: 'Precisa de ajuda com sua assinatura? Entre em contato conosco em ',
    payment_divider_or: 'ou',
    
    // Status de pagamento manual
    pending_payment_review: 'Pagamento em Revisão',
    payment_under_review: 'Pagamento em Revisão',
    manual_payment_review_message: 'Seu comprovante de pagamento manual foi recebido e está sendo revisado por nossa equipe.',
    amount: 'Valor',
    submitted: 'Enviado',
    pending_review: 'Pendente de Revisão',
    manual_payment_timeline: 'Pagamentos manuais são tipicamente revisados dentro de 24 horas em dias úteis.',
    
    // Página de pagamento manual enviado
    manual_payment_submitted_title: 'Comprovante de Pagamento Enviado',
    manual_payment_received: 'Comprovante de Pagamento Recebido!',
    manual_payment_under_review: 'Seu pagamento está em revisão',
    manual_payment_review_explanation: 'Nossa equipe verificará seu pagamento e ativará sua assinatura dentro de 24 horas em dias úteis.',
    payment_submitted_via: 'Pagamento enviado via',
    manual_payment_timeline_detailed: 'Tempo típico de revisão: 4-24 horas em dias úteis',
    manual_payment_registration_message: 'Crie sua conta agora para receber notificações sobre o status do seu pagamento e acessar sua assinatura uma vez aprovada.',
    manual_payment_benefit_1: 'Receba notificações quando seu pagamento for aprovado',
    manual_payment_benefit_2: 'Acompanhe seu progresso de aprendizado',
    manual_payment_benefit_3: 'Gerencie as configurações da sua assinatura',
    manual_payment_note_1: 'Você receberá confirmação por email uma vez que seu pagamento seja aprovado',
    manual_payment_note_2: 'Sua assinatura será ativada automaticamente após a aprovação',
    manual_payment_note_3: 'Use o mesmo email que você forneceu para o pagamento ao se registrar',
    
    // Traduções de pagamento de convidado
    enter_email_to_continue: 'Digite seu email para continuar',
    your_email_address: 'Seu endereço de email',
    please_enter_valid_email: 'Por favor, digite um email válido',
    subscription_creation_error: 'Erro ao criar assinatura',
    already_have_account: 'Já tem uma conta?',
    credit_card: 'Cartão de Crédito',
    choose_payment_method: 'Escolher Método de Pagamento',
    pay_with_credit_debit_card: 'Pagar com Cartão de Crédito/Débito',
    manual_payment_label: '(Pagamento Manual)',
    enter_email_to_continue_checkout: 'Por favor, digite seu endereço de email acima para continuar com o pagamento.',
    back: '← Voltar',
    complete_your_payment: 'Complete Seu Pagamento',
    
    // Mensagens de popup de validação
    requirements_not_met: 'Requisitos Não Atendidos',
    please_complete_following: 'Por favor, complete o seguinte para continuar:',
    enter_email_required: 'Digite seu endereço de email acima',
    select_6_month_yearly: 'Selecione um plano de 6+ meses',
    manual_payments_duration_required: 'Pagamentos manuais só estão disponíveis para planos de 6+ meses',
    paypal_not_available_usd: 'Pagamento manual do PayPal não está disponível para moeda USD',
    continue_button: 'Continuar',
    got_it: 'Entendi',
    dismiss: 'Fechar',
    
    // Checkout de email aprimorado
    email_checkout_options: 'Opções de Pagamento',
    email_sync_explanation: 'Como funciona:',
    email_sync_details: 'Use o mesmo email para pagar agora e se registrar depois. Sua assinatura será sincronizada automaticamente quando você criar sua conta.',
    prefer_register_first: 'Prefere se registrar primeiro?',
    already_have_account_short: 'Tem conta?',
    accept_terms_prefix: 'Aceito os',
    terms_and_conditions: 'Termos e Condições',
    accept_terms_suffix: 'e concordo com os termos de assinatura.',
    subscription_terms_notice: 'Ao se inscrever, você aceita os {TERMS_LINK}',
    subscription_terms_prefix: 'Ao se inscrever, você aceita os',
    
    // Páginas de Sucesso/Cancelamento
    processing_payment: 'Processando Pagamento',
    please_wait_processing: 'Por favor, aguarde enquanto processamos seu pagamento...',
    payment_successful: 'Pagamento Bem-sucedido!',
    subscription_activated: 'Sua Assinatura Foi Ativada!',
    guest_checkout_success_message: 'Seu pagamento foi processado com sucesso. Agora você pode completar seu registro para acessar todo o conteúdo.',
    complete_registration: 'Completar Registro',
    guest_registration_explanation: 'Crie sua conta para acessar todos os recursos:',
    access_all_content: 'Acesso a todo o conteúdo',
    track_progress: 'Acompanhamento de progresso',
    manage_subscription: 'Gerenciar assinatura',
    create_account: 'Criar Conta',
    existing_user: 'Usuário Existente',
    existing_user_explanation: 'Já tem uma conta com este email?',
    login_to_access: 'Entrar para Acessar',
    important_note: 'Nota Importante',
    email_sync_reminder: 'Use o mesmo email com o qual você pagou para que sua assinatura seja sincronizada automaticamente.',
    questions_contact_support: 'Tem dúvidas? Entre em contato conosco em suporte@profezac.com',
    back_to_home: 'Voltar ao Início',
    subscription_success_message: 'Sua assinatura está agora ativa e você pode acessar todo o conteúdo.',
    go_to_dashboard: 'Ir ao Painel',
    session_id: 'ID da Sessão',
    payment_cancelled: 'Pagamento Cancelado',
    subscription_not_activated: 'Sua Assinatura Não Foi Ativada',
    guest_checkout_cancelled_message: 'Você cancelou o processo de pagamento. Nenhuma cobrança foi aplicada ao seu método de pagamento.',
    no_charges_applied: 'Nenhuma cobrança foi aplicada à sua conta.',
    
    // Página de Assinatura
    loading_pricing: 'Carregando Preços...',
    subscription_required: 'Assinatura Necessária',
    subscription_required_message: 'Por favor, faça login para ver as opções de assinatura.',
    log_in: 'Entrar',
    choose_learning_plan: 'Escolha Seu Plano de Acesso',
    all_plans_include: 'Todos os planos incluem',
    unlock_english_potential: 'Desbloqueie seu potencial em inglês com nossos cursos completos em vídeo',
    prices_shown_in: 'Preços mostrados em %s para %s',
    change_currency: 'Mudar Moeda:',
    loading_currencies: 'Carregando moedas...',
    active_subscription: 'Assinatura Ativa',
    current_access_level: 'Você atualmente tem acesso ao conteúdo de nível %s',
    expires: 'Expira: %s',
    weekly: 'Semanal',
    monthly: '1 mês',
    yearly: '1 ano',
    save_percentage: 'Economize %s!',
    best_value: 'MELHOR VALOR',
    week: 'semana',
    month: 'mês',
    year: 'ano',
    more_features: '+%s recursos a mais',
    selected_plan: 'Plano Selecionado',
    processing: 'Processando...',
    pay_with_card: 'Pagar com Cartão',
    mercadopago: 'MercadoPago',
    paypal: 'PayPal',
    secure_payment_cancel: 'Pagamento seguro • Cancele a qualquer momento',
    manual_transfer_note: 'Transferência Manual: Carregue captura de tela do pagamento para aprovação rápida',
    mercadopago_yearly_only: 'Pagamentos MercadoPago estão disponíveis apenas para planos anuais',
    bancolombia_yearly_only: 'Pagamentos Bancolombia estão disponíveis apenas para planos anuais',
    manual_payments_yearly_only: 'Pagamentos manuais estão disponíveis apenas para planos anuais',
    mercadopago_sixmonth_yearly_only: 'Pagamentos MercadoPago estão disponíveis apenas para planos de 6 meses e anuais',
    bancolombia_sixmonth_yearly_only: 'Pagamentos Bancolombia estão disponíveis apenas para planos de 6 meses e anuais',
    manual_payments_sixmonth_yearly_only: 'Pagamentos manuais estão disponíveis apenas para planos de 6 meses e anuais',
    six_month_plans_only: 'Disponível apenas para planos de 6 meses ou mais',
    manual_payment_button: 'Pagamento Manual',
    
    // Pagamento Manual PayPal
    paypal_manual_payment: 'Pagamento Manual via PayPal',
    paypal_payment_instructions: 'Instruções de Pagamento',
    paypal_step_1: 'Abra seu app PayPal ou visite paypal.com',
    paypal_step_2: 'Envie dinheiro para: asistente.zachmorris@gmail.com',
    paypal_step_3: 'Inclua os detalhes do seu plano na nota',
    paypal_step_4: 'Tire uma captura de tela da confirmação do pagamento',
    paypal_step_5: 'Carregue a captura de tela abaixo',
    paypal_send_to: 'Enviar pagamento para:',
    paypal_include_note: 'Inclua os detalhes do plano na nota:',
    paypal_clear_screenshot: 'Tire uma captura de tela clara da confirmação do pagamento',
    paypal_review_time: 'Seu acesso será aprovado em menos de 24 horas',
    paypal_confirmation_screenshot: 'Captura de Tela de Confirmação de Pagamento',
    paypal_select_screenshot: 'Selecionar captura',
    paypal_change_file: 'Alterar arquivo',
    paypal_payment_summary: 'Resumo do Pagamento:',
    paypal_payment_to: 'Pagamento para:',
    paypal_currency: 'Moeda:',
    paypal_submit_payment: 'Enviar Pagamento',
    paypal_submitting: 'Enviando...',
    paypal_cancel: 'Cancelar',
    paypal_payment_submitted: 'Pagamento Enviado!',
    paypal_submit_success: 'Pagamento enviado com sucesso! Revisaremos seu pagamento dentro de 24 horas.',
    paypal_close: 'Fechar',
    paypal_upload_required: 'Por favor, carregue uma captura de tela da confirmação do seu pagamento PayPal',
    paypal_file_too_large: 'Arquivo muito grande. Máximo 10MB.',
    paypal_invalid_file: 'Apenas arquivos de imagem são permitidos (JPEG, PNG, GIF, WebP)',
    paypal_submit_error: 'Erro ao enviar pagamento',
    why_choose_profezac: 'Por Que Escolher Profezac?',
    interactive_videos: 'Vídeos Interativos',
    interactive_videos_desc: 'Aprenda com conteúdo de vídeo profissional e envolvente',
    smart_quizzes: 'Questionários Inteligentes',
    smart_quizzes_desc: 'Teste seu conhecimento com questionários alimentados por IA',
    transcriptions: 'Transcrições',
    transcriptions_desc: 'Leia junto com transcrições com marcações de tempo',
    progress_tracking: 'Acompanhamento de Progresso',
    progress_tracking_desc: 'Monitore sua jornada de aprendizado',
    back_to_dashboard: 'Voltar ao Painel Principal',
    
    // Planos de Assinatura
    basic_english: 'Inglês Básico - Do Zero',
    basic_english_desc: 'Nenhum conhecimento prévio necessário. Comece a aprender inglês desde o primeiro dia',
    advanced_english: 'Inglês Avançado',
    advanced_english_desc: 'Para estudantes intermediários prontos para avançar suas habilidades',
    native_level: 'Nível Nativo',
    native_level_desc: 'Domine inglês de nível nativo com conteúdo avançado',
    complete_access: 'Acesso Completo',
    complete_access_desc: 'Acesso completo a todos os níveis - Melhor Valor!',
    
    // Recursos dos Planos
    access_basic_videos: 'Acesso ao nível Básico',
    basic_selling_point: 'Profe Zac decompôs o idioma inglês palavra por palavra, para que QUALQUER PESSOA possa entendê-lo - Nenhum conhecimento prévio de inglês necessário',
    interactive_quizzes_timestamps: 'Quizzes que garantem compreensão',
    transcriptions_after_quiz: 'Legendas que acompanham todos os vídeos',
    progress_tracking_feature: 'Acompanhamento de progresso',
    learn_180_key_words: 'As 180 palavras-chave para entender a maioria do inglês falado',
    videos_make_english_easy: 'Vídeos que tornam o inglês fácil de entender',
    access_any_device: 'Acesso de qualquer dispositivo',
    everything_in_basic: 'Tudo do Básico',
    access_advanced_videos: 'Acesso ao nível Avançado',
    advanced_selling_point: 'Construindo sobre o nível Básico, estes vídeos usam as MESMAS palavras com menos contexto, exigindo mais compreensão mas com rede de segurança',
    advanced_vocabulary_grammar: 'Vocabulário e gramática avançados',
    complex_conversation_scenarios: 'Cenários de conversa complexos',
    priority_support: 'Suporte prioritário',
    everything_in_advanced: 'Tudo do Avançado',
    access_native_videos: 'Acesso ao nível Nativo',
    native_selling_point: 'As MESMAS palavras do Básico e Avançado mas SEM contexto audiovisual - exposição desafiante que acelera seu processo de aprendizagem dramaticamente',
    native_speaker_expressions: 'Expressões de falante nativo',
    cultural_context_idioms: 'Contexto cultural e expressões idiomáticas',
    advanced_pronunciation_training: 'Treinamento avançado de pronúncia',
    access_all_levels: 'Acesso a TODOS os níveis (Básico + Avançado + Nativo)',
    all_levels_selling_point: 'Acesso aos 3 níveis lhe dará a experiência completa - desde entender inglês apenas com contexto até dominá-lo sem contexto. Recomendado para aqueles que querem acelerar sua aprendizagem',
    
    // Plan title suffixes
    advanced_title_suffix: 'Aprofundar',
    native_title_suffix: 'Domínio',
    all_levels_title_suffix: 'Aprendizagem Completa',
    
    unlimited_video_access: 'Acesso ilimitado a vídeos',
    unlimited_quiz_attempts: 'Tentativas ilimitadas de questionários',
    all_transcriptions_included: 'Todas as transcrições incluídas',
    new_content_released: 'Novo conteúdo quando for lançado',
    
    // Rótulos de Duração
    per_week: 'por semana',
    per_month: 'por mês',
    per_year: 'por ano',
    
    // Rótulos Adicionais da Página de Assinatura
    select_plan: 'Por favor, selecione um plano de assinatura',
    pricing_load_error: 'Não é possível carregar informações de preços. Por favor, atualize a página.',
    currency_change_error: 'Não é possível alterar a moeda. Tente novamente.',
    
    // Validação de Formulário de Registro
    passwords_do_not_match: 'As senhas não coincidem',
    password_too_short: 'A senha deve ter pelo menos 6 caracteres',
    
    // Inspiração Diária
    daily_inspiration_title: 'Mensagem do Dia do Profe Zac',
    daily_inspiration_loading: 'Carregando inspiração de hoje...',
    daily_inspiration_error: 'Não foi possível carregar a mensagem diária',
    daily_inspiration_try_again: 'Tentar Novamente',
    daily_inspiration_refresh: 'Atualizar',
    daily_inspiration_video_message: 'Mensagem diária em vídeo',
    daily_inspiration_text_message: 'Texto de inspiração diária',
    daily_inspiration_today_goal: 'Meta de Hoje',
    daily_inspiration_today_goal_text: 'Pratique uma nova frase ou conversa em inglês hoje!',
    daily_inspiration_remember: 'Lembre-se',
    daily_inspiration_remember_text: 'Cada passo adiante é progresso. ¡Tú puedes!',
    daily_inspiration_personalized: 'Recomendação personalizada',
    daily_inspiration_new_lesson: 'Nova lição para você!',
    daily_inspiration_continue_progress: 'Continue seu progresso!',
    daily_inspiration_go_to_lesson: 'Ir para a lição completa',
    daily_inspiration_show_inspiration: 'Inspiração diária',
    
    // Home page join section
    ready_to_start: 'Pronto para Começar a Aprender?',
    join_now: 'JUNTE-SE AGORA',
    home_join_description: 'Comece a aprender uma palavra em inglês por dia e torne o inglês mais fácil todos os dias. Este é o único curso feito por um falante nativo - projetado para ensinar as palavras mais comuns em inglês.',
    
    // Preview overlay translations
    preview_unlock_text: 'Desbloqueie acesso a este vídeo e mais de 180 lições com questionários interativos',
    get_full_access: 'Obter Acesso Completo',
    subscription_modal_text: 'Junte-se a milhares dominando o inglês com nosso curso completo!',
    subscription_videos_count: '180+ vídeos',
    subscription_interactive_quizzes: 'Questionários interativos',
    subscription_all_levels: 'Todos os níveis',
    start_learning_today: 'Comece a Aprender Hoje',
    start_course: 'Começar a Aprender Inglês Desde 0',
    learn_english_from_zero_with_profe_zac: 'Aprenda Inglês Desde 0 com Profe Zac',
    about_to_start_conversations: 'Você está prestes a começar a entender conversas em inglês',
    want_to_learn_english_from_zero: 'Quero aprender inglês desde o zero',
    motivational_text_base: 'Se até agora você não conseguiu aprender inglês, não é porque você não pode, é porque ninguém te ensinou da maneira correta. E aqui é onde entra o Inglês com Profe Zac. Este curso foi criado com um propósito: tornar o aprendizado de inglês fácil, acessível e eficaz para',
    latino_community_default: 'a comunidade latina',
    ready_for_more: 'Pronto para mais?',
    sample_lesson_text: 'Lição de Inglês de Amostra',
    shuffle_video: 'Próximo Vídeo',
    
    // Sincronização de Associação
    sync_paypal: 'Sincronizar Membresía',
    sync_membership: 'Sincronizar Associação',
    sync_paypal_membership: 'Vincular Sua Assinatura',
    link_paypal_subscription: 'Vincular Sua Assinatura',
    membership_sync_desc: 'Se você tem uma assinatura ativa com PayPal, MercadoPago, etc. que não está aparecendo na sua conta, use este formulário para vinculá-la. Nossa equipe verificará seu pagamento e concederá acesso.',
    subscription_details_optional: 'Detalhes da Assinatura (Opcional)',
    subscription_id_label: 'ID da Assinatura',
    paypal_subscription_id: 'ID da Assinatura PayPal',
    subscription_id_placeholder: 'I-XXXXXXXXX (se você tem)',
    subscription_id_help: 'Isso pode ser encontrado na sua conta PayPal em "Pagamentos Automáticos"',
    payment_information: 'Informações de Pagamento',
    amount_paid: 'Valor Pago',
    currency: 'Moeda',
    duration: 'Duração',
    course_access_level: 'Nível de Acesso ao Curso',
    payment_proof_optional: 'Comprovante de Pagamento (Opcional)',
    upload_payment_proof: 'Faça upload de uma captura de tela do seu pagamento ou recibo (PNG, JPG, PDF - máx 5MB)',
    additional_notes: 'Notas Adicionais',
    additional_notes_placeholder: 'Qualquer informação adicional sobre sua assinatura...',
    review_process: 'Processo de Revisão:',
    review_process_desc: 'Nossa equipe revisará sua solicitação em 1-2 dias úteis. Você receberá uma notificação por email uma vez aprovada.',
    usd_dollar: 'USD - Dólar Americano',
    eur_euro: 'EUR - Euro',
    ars_peso: 'ARS - Peso Argentino',
    cop_peso: 'COP - Peso Colombiano',
    mxn_peso: 'MXN - Peso Mexicano',
    brl_real: 'BRL - Real Brasileiro',
    gbp_pound: 'GBP - Libra Esterlina',
    cad_dollar: 'CAD - Dólar Canadense',
    submit_request: 'Enviar Solicitação',
    submitting: 'Enviando...',
    cancel: 'Cancelar',
    your_previous_requests: 'Suas Solicitações Anteriores',
    amount: 'Valor',
    level: 'Nível',
    status: 'Status',
    submitted: 'Enviado',
    pending_review: 'Pendente de Revisão',
    approved: 'Aprovado',
    denied: 'Negado',
    under_review: 'Em Revisão',
    request_submitted_success: '✅ Solicitação de sincronização enviada com sucesso! Nossa equipe a revisará em 1-2 dias úteis.',
    monthly: '1 mês',
    yearly: '1 ano',
    semiannual: '6 Meses',
    lifetime: 'Vitalício',
    basic_level: 'Nível Básico',
    advanced_level: 'Nível Avançado',
    native_level: 'Nível Nativo',
    all_levels: 'Todos os Níveis',
    
    // Termos e Condições
    terms_title: 'Termos de Serviço',
    terms_intro: 'Por favor leia estes termos cuidadosamente antes de usar nosso serviço.',
    terms_section_1_title: '1. Acesso Imediato ao Curso',
    terms_section_1_p1: 'Uma vez que você faça sua compra, você terá acesso imediato através do portal.',
    terms_section_1_p2: 'Todo o conteúdo do curso está disponível através da plataforma do portal.',
    terms_section_2_title: '2. Planos de Assinatura e Cancelamento',
    terms_section_2_p1: 'Oferecemos planos de uma semana, um mês e um ano. Uma vez que você tenha adquirido o plano, o acesso é concedido durante o período que foi comprado.',
    terms_section_2_p2: 'Sua assinatura estará ativa desde o momento em que você comprou - ou seja, desde o momento em que você tem acesso ao curso, seu período de compra está ativo.',
    terms_section_2_p3: 'Para cancelar sua assinatura, simplesmente vá ao painel de controle e dentro do dashboard do usuário você encontrará a seção onde gerencia sua assinatura.',
    terms_section_2_p4: 'Mesmo que você tenha cancelado sua assinatura, você ainda terá acesso até o final do período pelo qual seu plano foi comprado.',
    terms_section_2_p5: 'É sua responsabilidade cancelar sua assinatura antes da data de renovação para evitar ser cobrado automaticamente pelo próximo ciclo de faturamento.',
    terms_section_3_title: '3. Política de Não Reembolso e Não Devolução',
    terms_section_3_p1: 'Devido ao alto valor e acessibilidade do nosso serviço, não oferecemos reembolsos nem devoluções.',
    terms_section_3_p2: 'Recomendamos adquirir o plano semanal ou mensal inicialmente para avaliar o curso antes de optar por planos mais caros.',
    terms_section_3_p3: 'Ao comprar o curso, você confirma que entende que todas as vendas são finais e nenhum reembolso será emitido.',
    terms_section_3_p4: 'Contestar uma cobrança legítima pode resultar na suspensão ou encerramento da sua conta.',
    terms_section_4_title: '4. Métodos de Pagamento',
    terms_section_4_p1: 'Os métodos de pagamento aceitos incluem cartões de crédito/débito, PayPal, Bitcoin, Mercado Pago e Bancolombia.',
    terms_section_4_p2: 'Métodos de pagamento locais também são oferecidos em alguns países.',
    terms_section_5_title: '5. Acesso ao Conteúdo e Propriedade',
    terms_section_5_p1: 'Quando você compra um plano de assinatura, você está pagando pelo acesso ao nosso conteúdo educacional, incluindo vídeos, questionários e transcrições. Esta assinatura concede a você o direito de acessar e usar os materiais do curso durante seu período ativo de assinatura.',
    terms_section_5_p2: 'Note que o conteúdo do curso, incluindo todos os vídeos, questionários, transcrições e materiais relacionados, permanecem propriedade intelectual de English with Profezac. Sua assinatura não transfere a propriedade de qualquer conteúdo para você, e você não pode redistribuir, copiar ou compartilhar os materiais do curso com outros.',
    terms_section_6_title: '6. Descontos e Promoções',
    terms_section_6_content: 'Marcador de posição para o conteúdo da seção 6 - será atualizado.',
    terms_section_7_title: '7. Filosofia de Aprendizagem',
    terms_section_7_content: 'Marcador de posição para o conteúdo da seção 7 - será atualizado.',
    terms_section_8_title: '8. Privacidade e Segurança de Dados',
    terms_section_8_content: 'Marcador de posição para o conteúdo da seção 8 - será atualizado.',
    terms_section_9_title: '9. Aceitação dos Termos',
    terms_section_9_p1: 'Ao adquirir e acessar o curso, você reconhece e aceita estes Termos de Serviço.',
    terms_section_9_p2: 'Estes termos estão sujeitos a alterações, e as atualizações serão publicadas em nosso site oficial.'
  }
};

// Create the language context
const LanguageContext = createContext();

// Provider component
export const LanguageProvider = ({ children }) => {
  // Get stored language or default to Spanish
  const [language, setLanguage] = useState(() => {
    const storedLanguage = localStorage.getItem('preferred_language');
    
    // Debug logging
    console.log('Language Context Debug:', {
      storedLanguage,
      url: window.location.href,
      search: window.location.search,
      referrer: document.referrer
    });
    
    // ALWAYS check URL parameters first (highest priority)
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    
    console.log('URL lang parameter:', langParam);
    
    if (langParam) {
      const normalizedLang = langParam.toLowerCase();
      if (normalizedLang === 'pt' || normalizedLang === 'portuguese') {
        console.log('Setting language to PT from URL parameter');
        return LANGUAGES.PT;
      } else if (normalizedLang === 'en' || normalizedLang === 'english') {
        console.log('Setting language to EN from URL parameter');
        return LANGUAGES.EN;
      } else if (normalizedLang === 'es' || normalizedLang === 'spanish') {
        console.log('Setting language to ES from URL parameter');
        return LANGUAGES.ES;
      }
    }
    
    // If no URL parameter, check stored language
    if (storedLanguage) {
      console.log('Using stored language:', storedLanguage);
      return storedLanguage;
    }
    
    // Fallback to referrer check for Portuguese site
    const referrer = document.referrer;
    if (referrer && referrer.includes('profzac.com')) {
      console.log('Setting language to PT from referrer:', referrer);
      return LANGUAGES.PT; // Set Portuguese for users from profzac.com
    }
    
    const finalLanguage = LANGUAGES.ES;
    console.log('Final language selected:', finalLanguage);
    return finalLanguage;
  });

  // Update localStorage when language changes
  useEffect(() => {
    console.log('Language updated, saving to localStorage:', language);
    localStorage.setItem('preferred_language', language);
  }, [language]);

  // Translate function - gets a translation key and returns the translated text
  const translate = (key, ...params) => {
    // Get the translation for the current language
    const translation = translations[language][key];
    
    // If translation doesn't exist, fallback to English or just return the key
    if (!translation) {
      const fallback = translations[LANGUAGES.EN][key];
      return fallback || key;
    }
    
    // Handle string formatting with parameters (like %s replacements)
    if (params.length > 0 && translation.includes('%s')) {
      let formattedTranslation = translation;
      params.forEach(param => {
        formattedTranslation = formattedTranslation.replace('%s', param);
      });
      return formattedTranslation;
    }
    
    return translation;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, translate, LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for using language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;